import { Color } from './java/Color';
import { Graphics2D, Font, FontMetrics, BasicStroke, RenderingHints } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { Random } from './java/Random';
import { MouseEvent, MouseWheelEvent } from './java/AwtEvent';

import { STATE } from './STATE';
import { quitGame } from './quit';
import { Game } from './Game';
import { Handler } from './Handler';
import { HUD } from './HUD';
import { GameObject } from './GameObject';
import { ID } from './ID';
import { PageRenderer } from './PageRenderer';
import { Settings } from './Settings';
import { Profile } from './Profile';
import { Achievements, Achievement } from './Achievements';
import { CoinShop, Item } from './CoinShop';
import { DailyChallenge } from './DailyChallenge';
import { Perks } from './Perks';
import { PlayerSkins } from './PlayerSkins';
import { Trail } from './Trail';
import { AudioPlayer } from './AudioPlayer';
import { GamePalette } from './GamePalette';
import { Player } from './Player';
import { BasicEnemy } from './BasicEnemy';
import { HardEnemy } from './HardEnemy';

// Settings drag
enum SettingsDrag { NONE, MUSIC_VOL, SFX_VOL }

export class Menu {

  private game: Game;
  private handler: Handler;
  private hud: HUD;

  // Fixed dimensions
  private static get BW(): number { return PageRenderer.BTN_W; }
  private static get BH(): number { return PageRenderer.BTN_H; }
  private static get SP(): number { return PageRenderer.BTN_SPACING; }
  private static readonly LANG_W = 330;
  private static readonly MUSIC_W = 170;
  private static readonly MUSIC_H = 34;
  private static readonly MUSIC_Y = 668;
  private static readonly RETRY_W = 300;
  private static readonly RETRY_H = 50;
  private static readonly RETRY_Y = 530;

  // Y positions — 5 main menu buttons
  private static readonly MENU_SP = 58;
  private static readonly PLAY_Y = 260;
  private static readonly PROFILE_MENU_Y = Menu.PLAY_Y + Menu.MENU_SP;
  private static readonly SHOP_MENU_Y = Menu.PROFILE_MENU_Y + Menu.MENU_SP;
  private static readonly SETTINGS_MENU_Y = Menu.SHOP_MENU_Y + Menu.MENU_SP;
  private static readonly HELP_MENU_Y = Menu.SETTINGS_MENU_Y + Menu.MENU_SP;

  // Quit icon (top-right)
  private static readonly QUIT_SIZE = 34;
  private static readonly QUIT_Y = 24;
  private static quitIconX(): number { return Game.WIDTH - Menu.QUIT_SIZE - 24; }

  // Music icon (bottom-left)
  private static readonly MUSIC_ICON_SIZE = 26;
  private static readonly MUSIC_ICON_Y = 632;

  // Legacy aliases — used by Select/Help sub-screens (3-button layouts)
  private static get INFO_Y(): number { return Menu.PLAY_Y + Menu.SP; }
  private static get HELP_Y(): number { return Menu.INFO_Y + Menu.SP; }
  private static get COMBAT_Y(): number { return Menu.HELP_Y + Menu.SP; }

  // Pause menu layout
  private static readonly PAUSE_W = 260;
  private static readonly PAUSE_H = 46;
  private static readonly PAUSE_SP = 60;
  private static readonly PAUSE_Y0 = 310;
  private static pauseX(): number { return Math.trunc((Game.WIDTH - Menu.PAUSE_W) / 2); }

  // Dynamic X positions
  private static btnX(): number { return PageRenderer.btnX(); }
  private static langX(): number { return Math.trunc((Game.WIDTH - Menu.LANG_W) / 2); }
  private static musicX(): number { return Game.WIDTH - 220; }
  private static quitX(): number { return PageRenderer.backX(); }
  private static retryX(): number { return Math.trunc((Game.WIDTH - Menu.RETRY_W) / 2); }

  // Hover state
  private btn: number[] = new Array<number>(6).fill(0);
  private backH = 0;
  private musicH = 0;
  private shopH = 0;
  private dailyH = 0;
  private aboutH = 0;
  private changelogH = 0;
  private creditsLinkH = 0;
  private quitH = 0;
  private retryH = 0;
  private static readonly SHOP_BTN_W = 170;
  private static readonly SHOP_BTN_H = 34;
  private static readonly SHOP_BTN_Y = 668;
  private static readonly DAILY_BTN_W = 140;
  private static readonly DAILY_BTN_H = 34;
  private static readonly DAILY_BTN_Y = 668;
  private static dailyBtnX(): number { return 30 + Menu.SHOP_BTN_W + 12; }
  private pauseBtn: number[] = new Array<number>(4).fill(0);
  private mouseX = 0;
  private mouseY = 0;
  private static readonly LERP = 0.14;

  // Help page scroll
  private helpScroll = 0;
  private helpScrollTarget = 0;

  // Settings page state

  private settingsScroll = 0;
  private settingsScrollTarget = 0;

  // Settings hover states: 0=musicVol, 1=sfxVol, 2=shakeOff, 3=shakeLow, 4=shakeHigh,
  // 5=partLow, 6=partMed, 7=partHigh, 8=fpsToggle, 9=trailToggle, 10=gridToggle,
  // 11=colorblindToggle, 12-17=lang buttons, 18=resetScores, 19=resetAll
  private static readonly SET_HOVER_COUNT = 20;
  private setH: number[] = new Array<number>(Menu.SET_HOVER_COUNT).fill(0);
  private setMusicBarH = 0;
  private setSfxBarH = 0;

  private settingsDrag: SettingsDrag = SettingsDrag.NONE;

  // Confirmation dialog
  private confirmAction: string | null = null; // "scores" or "progress" or null
  private confirmYesH = 0;
  private confirmNoH = 0;

  // Track where Settings was opened from (null = main menu)
  public static settingsReturnTo: STATE | null = null;

  constructor(game: Game, handler: Handler, hud: HUD) {
    this.game = game;
    this.hud = hud;
    this.handler = handler;
  }

  // ==================== Hover ====================

  public tick(): void {
    this.updateHover();
    // Smooth scroll
    this.helpScroll += (this.helpScrollTarget - this.helpScroll) * 0.18;
    if (Math.abs(this.helpScroll - this.helpScrollTarget) < 0.5) this.helpScroll = this.helpScrollTarget;
    this.settingsScroll += (this.settingsScrollTarget - this.settingsScroll) * 0.18;
    if (Math.abs(this.settingsScroll - this.settingsScrollTarget) < 0.5) this.settingsScroll = this.settingsScrollTarget;
  }

  private updateHover(): void {
    // Determine targets based on current state
    const btnTargets: boolean[] = new Array<boolean>(6).fill(false);
    let backTarget = false, musicTarget = false;
    const shopTarget = false;
    let dailyTarget = false, aboutTarget = false;
    let changelogTarget = false, creditsLinkTarget = false, quitTarget = false, retryTarget = false;

    let bx: number;

    switch (Game.gameState) {
      case STATE.Menu: {
        bx = Menu.btnX();
        btnTargets[0] = this.hit(this.mouseX, this.mouseY, bx, Menu.PLAY_Y, Menu.BW, Menu.BH);
        btnTargets[1] = this.hit(this.mouseX, this.mouseY, bx, Menu.PROFILE_MENU_Y, Menu.BW, Menu.BH);
        btnTargets[2] = this.hit(this.mouseX, this.mouseY, bx, Menu.SHOP_MENU_Y, Menu.BW, Menu.BH);
        btnTargets[3] = this.hit(this.mouseX, this.mouseY, bx, Menu.SETTINGS_MENU_Y, Menu.BW, Menu.BH);
        btnTargets[4] = this.hit(this.mouseX, this.mouseY, bx, Menu.HELP_MENU_Y, Menu.BW, Menu.BH);
        quitTarget = this.hit(this.mouseX, this.mouseY, Menu.quitIconX(), Menu.QUIT_Y, Menu.QUIT_SIZE, Menu.QUIT_SIZE);
        musicTarget = this.hit(this.mouseX, this.mouseY, 24, 20, Menu.MUSIC_ICON_SIZE, Menu.MUSIC_ICON_SIZE);
        dailyTarget = this.hit(this.mouseX, this.mouseY, Game.WIDTH - 204, 688, 180, 18);
        const linkXs = this.getBottomLinkXs();
        aboutTarget = this.hit(this.mouseX, this.mouseY, linkXs[0], 666, linkXs[1] - linkXs[0], 20);
        changelogTarget = this.hit(this.mouseX, this.mouseY, linkXs[1], 666, linkXs[2] - linkXs[1], 20);
        creditsLinkTarget = this.hit(this.mouseX, this.mouseY, linkXs[2], 666, linkXs[3] - linkXs[2], 20);
        break;
      }
      case STATE.Profile: {
        bx = Menu.btnX();
        btnTargets[0] = this.hit(this.mouseX, this.mouseY, bx, 200, Menu.BW, Menu.BH);
        btnTargets[1] = this.hit(this.mouseX, this.mouseY, bx, 258, Menu.BW, Menu.BH);
        btnTargets[2] = this.hit(this.mouseX, this.mouseY, bx, 316, Menu.BW, Menu.BH);
        backTarget = this.hitBack();
        break;
      }
      case STATE.Select: {
        bx = Menu.btnX();
        btnTargets[0] = this.hit(this.mouseX, this.mouseY, bx, Menu.PLAY_Y, Menu.BW, Menu.BH);
        btnTargets[1] = this.hit(this.mouseX, this.mouseY, bx, Menu.INFO_Y, Menu.BW, Menu.BH);
        btnTargets[2] = this.hit(this.mouseX, this.mouseY, bx, Menu.HELP_Y, Menu.BW, Menu.BH);
        btnTargets[3] = this.hit(this.mouseX, this.mouseY, bx, Menu.COMBAT_Y, Menu.BW, Menu.BH);
        backTarget = this.hitBack();
        break;
      }
      case STATE.Help: {
        const lx = Menu.langX();
        btnTargets[0] = this.hit(this.mouseX, this.mouseY, lx, Menu.PLAY_Y, Menu.LANG_W, Menu.BH);
        btnTargets[1] = this.hit(this.mouseX, this.mouseY, lx, Menu.INFO_Y, Menu.LANG_W, Menu.BH);
        btnTargets[2] = this.hit(this.mouseX, this.mouseY, lx, Menu.HELP_Y, Menu.LANG_W, Menu.BH);
        backTarget = this.hitBack();
        break;
      }
      case STATE.End: {
        retryTarget = this.hit(this.mouseX, this.mouseY, Menu.retryX(), Menu.RETRY_Y, Menu.RETRY_W, Menu.RETRY_H);
        backTarget = this.hitBack();
        break;
      }
      case STATE.Paused: {
        const px = Menu.pauseX();
        btnTargets[0] = this.hit(this.mouseX, this.mouseY, px, Menu.PAUSE_Y0, Menu.PAUSE_W, Menu.PAUSE_H);
        btnTargets[1] = this.hit(this.mouseX, this.mouseY, px, Menu.PAUSE_Y0 + Menu.PAUSE_SP, Menu.PAUSE_W, Menu.PAUSE_H);
        btnTargets[2] = this.hit(this.mouseX, this.mouseY, px, Menu.PAUSE_Y0 + Menu.PAUSE_SP * 2, Menu.PAUSE_W, Menu.PAUSE_H);
        this.pauseBtn[3] = this.approach(this.pauseBtn[3],
          this.hit(this.mouseX, this.mouseY, px, Menu.PAUSE_Y0 + Menu.PAUSE_SP * 3, Menu.PAUSE_W, Menu.PAUSE_H));
        break;
      }
      case STATE.Settings: {
        backTarget = this.hitBack();
        this.updateSettingsHover();
        break;
      }
      case STATE.Statistics:
      case STATE.AchievementsPage:
      case STATE.Customize:
      case STATE.Loadout:
      case STATE.CoinShopPage:
      case STATE.DailyPage: {
        backTarget = this.hitBack();
        break;
      }
      default: {
        backTarget = this.hitBack();
        break;
      }
    }

    for (let i = 0; i < 6; i++) this.btn[i] = this.approach(this.btn[i], btnTargets[i]);
    this.backH = this.approach(this.backH, backTarget);
    this.musicH = this.approach(this.musicH, musicTarget);
    this.shopH = this.approach(this.shopH, shopTarget);
    this.dailyH = this.approach(this.dailyH, dailyTarget);
    this.aboutH = this.approach(this.aboutH, aboutTarget);
    this.changelogH = this.approach(this.changelogH, changelogTarget);
    this.creditsLinkH = this.approach(this.creditsLinkH, creditsLinkTarget);
    this.quitH = this.approach(this.quitH, quitTarget);
    this.retryH = this.approach(this.retryH, retryTarget);
  }

  private approach(current: number, target: boolean): number {
    const t = target ? 1 : 0;
    current += (t - current) * Menu.LERP;
    if (Math.abs(current - t) < 0.01) current = t;
    return current;
  }

  // ==================== Input ====================

  public mouseMoved(e: MouseEvent): void {
    this.mouseX = Game.toGameX(e.getX());
    this.mouseY = Game.toGameY(e.getY());
    Game.mouseGameX = this.mouseX;
    Game.mouseGameY = this.mouseY;
  }

  public mouseWheelMoved(e: MouseWheelEvent): void {
    if (Game.gameState === STATE.HelpENG || Game.gameState === STATE.HelpNLD
        || Game.gameState === STATE.HelpDEU) {
      this.helpScrollTarget += e.getWheelRotation() * 40;
      this.helpScrollTarget = Math.max(0, this.helpScrollTarget);
    }
    if (Game.gameState === STATE.Settings || Game.gameState === STATE.Statistics
        || Game.gameState === STATE.AchievementsPage
        || Game.gameState === STATE.CoinShopPage
        || Game.gameState === STATE.Update_Notes) {
      this.settingsScrollTarget += e.getWheelRotation() * 40;
      this.settingsScrollTarget = Math.max(0, this.settingsScrollTarget);
    }
  }

  public mouseDragged(e: MouseEvent): void {
    this.mouseX = Game.toGameX(e.getX());
    this.mouseY = Game.toGameY(e.getY());
    Game.mouseGameX = this.mouseX;
    Game.mouseGameY = this.mouseY;
    if (Game.gameState === STATE.Settings) {
      if (this.settingsDrag === SettingsDrag.MUSIC_VOL) {
        Settings.setMusicVolume(this.settingsSliderRatio(this.mouseX));
      } else if (this.settingsDrag === SettingsDrag.SFX_VOL) {
        Settings.setSfxVolume(this.settingsSliderRatio(this.mouseX));
      }
    }
  }

  public mouseReleased(e: MouseEvent): void {
    this.settingsDrag = SettingsDrag.NONE;
    if (e.getButton() === MouseEvent.BUTTON1) Game.mouseShootHeld = false;
  }

  public mousePressed(e: MouseEvent): void {
    const mx = Game.toGameX(e.getX());
    const my = Game.toGameY(e.getY());
    // hitBack() (and the confirm-dialog test) hit-test against the mouseX/mouseY fields, which
    // only mouseMoved/mouseDragged write. A mouse keeps those live by hovering; a finger never
    // hovers, so on touch they would still read 0,0 and every Back button would be dead. On
    // desktop this is a no-op: the hover that preceded the click already set the same values.
    this.mouseX = mx;
    this.mouseY = my;

    // Combat mode: left click to shoot (hold for auto-fire)
    if (e.getButton() === MouseEvent.BUTTON1 && Game.gameState === STATE.Game && Game.combatMode) {
      Game.mouseShootHeld = true;
      return;
    }

    if (Game.gameState === STATE.Paused) {
      const px = Menu.pauseX();
      // Resume
      if (this.hit(mx, my, px, Menu.PAUSE_Y0, Menu.PAUSE_W, Menu.PAUSE_H)) {
        Game.gameState = Game.pausedFrom;
        this.resetHover(); return;
      }
      // Main Menu
      if (this.hit(mx, my, px, Menu.PAUSE_Y0 + Menu.PAUSE_SP, Menu.PAUSE_W, Menu.PAUSE_H)) {
        this.resetForMenu();
        Game.gameState = STATE.Menu;
        return;
      }
      // Settings
      if (this.hit(mx, my, px, Menu.PAUSE_Y0 + Menu.PAUSE_SP * 2, Menu.PAUSE_W, Menu.PAUSE_H)) {
        Menu.settingsReturnTo = STATE.Paused;
        Game.gameState = STATE.Settings;
        this.resetHover(); return;
      }
      // Quit
      if (this.hit(mx, my, px, Menu.PAUSE_Y0 + Menu.PAUSE_SP * 3, Menu.PAUSE_W, Menu.PAUSE_H)) {
        quitGame();
      }
      return;
    }

    if (Game.gameState === STATE.Menu) {
      const bx = Menu.btnX();
      if (this.hit(mx, my, bx, Menu.PLAY_Y, Menu.BW, Menu.BH)) { Game.gameState = STATE.Select; this.resetHover(); return; }
      if (this.hit(mx, my, bx, Menu.PROFILE_MENU_Y, Menu.BW, Menu.BH)) { Game.gameState = STATE.Profile; this.resetHover(); return; }
      if (this.hit(mx, my, bx, Menu.SHOP_MENU_Y, Menu.BW, Menu.BH)) { Game.gameState = STATE.CoinShopPage; this.resetHover(); return; }
      if (this.hit(mx, my, bx, Menu.SETTINGS_MENU_Y, Menu.BW, Menu.BH)) { Game.gameState = STATE.Settings; this.resetHover(); return; }
      if (this.hit(mx, my, bx, Menu.HELP_MENU_Y, Menu.BW, Menu.BH)) { Game.gameState = STATE.Help; this.resetHover(); return; }
      if (this.hit(mx, my, 24, 20, Menu.MUSIC_ICON_SIZE, Menu.MUSIC_ICON_SIZE)) { Game.gameState = STATE.MusicPlayer; this.resetHover(); return; }
      if (this.hit(mx, my, Game.WIDTH - 204, 688, 180, 18)) { Game.gameState = STATE.DailyPage; this.resetHover(); return; }
      const linkXs = this.getBottomLinkXs();
      if (this.hit(mx, my, linkXs[0], 666, linkXs[1] - linkXs[0], 20)) {
        Game.gameState = STATE.About; this.resetHover(); return;
      }
      if (this.hit(mx, my, linkXs[1], 666, linkXs[2] - linkXs[1], 20)) {
        Game.gameState = STATE.Update_Notes; this.resetHover(); return;
      }
      if (this.hit(mx, my, linkXs[2], 666, linkXs[3] - linkXs[2], 20)) {
        Game.gameState = STATE.Credits; this.resetHover(); return;
      }
      if (this.hit(mx, my, Menu.quitIconX(), Menu.QUIT_Y, Menu.QUIT_SIZE, Menu.QUIT_SIZE)) { quitGame(); }
      return;
    }

    if (Game.gameState === STATE.Profile) {
      const bx = Menu.btnX();
      if (this.hit(mx, my, bx, 200, Menu.BW, Menu.BH)) { Game.gameState = STATE.Customize; this.resetHover(); return; }
      if (this.hit(mx, my, bx, 258, Menu.BW, Menu.BH)) { Game.gameState = STATE.Statistics; this.resetHover(); return; }
      if (this.hit(mx, my, bx, 316, Menu.BW, Menu.BH)) { Game.gameState = STATE.AchievementsPage; this.resetHover(); return; }
      if (this.hitBack()) { Game.gameState = STATE.Menu; this.resetHover(); return; }
      return;
    }

    if (Game.gameState === STATE.Select) {
      const bx = Menu.btnX();
      if (this.hit(mx, my, bx, Menu.PLAY_Y, Menu.BW, Menu.BH)) { this.pendingDifficulty = 0; Game.combatMode = false; Perks.clearLoadout(); Game.gameState = STATE.Loadout; this.resetHover(); return; }
      if (this.hit(mx, my, bx, Menu.INFO_Y, Menu.BW, Menu.BH)) { this.pendingDifficulty = 1; Game.combatMode = false; Perks.clearLoadout(); Game.gameState = STATE.Loadout; this.resetHover(); return; }
      if (this.hit(mx, my, bx, Menu.HELP_Y, Menu.BW, Menu.BH)) { this.pendingDifficulty = 2; Game.combatMode = false; Perks.clearLoadout(); Game.gameState = STATE.Loadout; this.resetHover(); return; }
      if (this.hit(mx, my, bx, Menu.COMBAT_Y, Menu.BW, Menu.BH)) { this.pendingDifficulty = 3; Game.combatMode = true; Perks.clearLoadout(); Game.gameState = STATE.Loadout; this.resetHover(); return; }
      if (this.hitBack()) { Game.gameState = STATE.Menu; this.resetHover(); return; }
      return;
    }

    if (Game.gameState === STATE.Help) {
      const lx = Menu.langX();
      if (this.hit(mx, my, lx, Menu.PLAY_Y, Menu.LANG_W, Menu.BH)) { Game.gameState = STATE.HelpENG; this.resetHover(); return; }
      if (this.hit(mx, my, lx, Menu.INFO_Y, Menu.LANG_W, Menu.BH)) { Game.gameState = STATE.HelpNLD; this.resetHover(); return; }
      if (this.hit(mx, my, lx, Menu.HELP_Y, Menu.LANG_W, Menu.BH)) { Game.gameState = STATE.HelpDEU; this.resetHover(); return; }
      if (this.hitBack()) { Game.gameState = STATE.Menu; this.resetHover(); return; }
      return;
    }

    if (Game.gameState === STATE.HelpENG || Game.gameState === STATE.HelpNLD
        || Game.gameState === STATE.HelpDEU || Game.gameState === STATE.HelpFRA
        || Game.gameState === STATE.HelpRUS || Game.gameState === STATE.HelpSPA) {
      if (this.hitBack()) { Game.gameState = STATE.Help; this.resetHover(); return; }
      return;
    }

    if (Game.gameState === STATE.Settings) {
      this.handleSettingsClick(mx, my);
      return;
    }

    if (Game.gameState === STATE.Statistics || Game.gameState === STATE.AchievementsPage) {
      if (this.hitBack()) { Game.gameState = STATE.Profile; this.resetHover(); }
      return;
    }

    if (Game.gameState === STATE.Customize) {
      this.handleCustomizeClick(mx, my);
      return;
    }

    if (Game.gameState === STATE.Loadout) {
      this.handleLoadoutClick(mx, my);
      return;
    }

    if (Game.gameState === STATE.CoinShopPage) {
      this.handleCoinShopClick(mx, my);
      return;
    }

    if (Game.gameState === STATE.DailyPage) {
      if (this.hitBack()) { Game.gameState = STATE.Menu; this.resetHover(); return; }
      // Play button
      const playX = Math.trunc((Game.WIDTH - 260) / 2);
      if (DailyChallenge.canPlay() && this.hit(mx, my, playX, Game.HEIGHT - 90, 260, 50)) {
        this.startDailyGame();
        return;
      }
      return;
    }

    if (Game.gameState === STATE.About
        || Game.gameState === STATE.Update_Notes || Game.gameState === STATE.Credits) {
      if (this.hitBack()) { Game.gameState = STATE.Menu; this.resetHover(); return; }
      return;
    }

    if (Game.gameState === STATE.End) {
      if (this.hit(mx, my, Menu.retryX(), Menu.RETRY_Y, Menu.RETRY_W, Menu.RETRY_H)) {
        this.resetForMenu();
        Game.gameState = STATE.Select;
        return;
      }
      if (this.hitBack()) {
        this.resetForMenu();
        Game.gameState = STATE.Menu;
        return;
      }
    }
  }

  /**
   * Touch hit slop. A logical pixel is roughly half a CSS pixel on a phone in landscape
   * (scale = viewportHeight/720), so hit-testing the drawn rect exactly — the Daily link at 18
   * tall, the footer links at 20, the music icon at 26, Back at 34 — leaves 9-18 CSS px targets
   * that a thumb cannot hit. Grow only the undersized ones, up to the standard button height
   * (BTN_H = 50), and never by more than the +12 TouchControls gives its own buttons: a larger
   * slop would close the 8px gap between stacked buttons and let a tap in the gap fire the one
   * above. Zero on desktop, so mouse hit-testing is unchanged.
   */
  private static readonly TOUCH_MIN = 50;
  private static readonly TOUCH_SLOP_MAX = 12;

  private static touchSlop(size: number): number {
    if (!Game.isTouchInput()) return 0;
    return Math.min(Menu.TOUCH_SLOP_MAX, Math.max(0, Math.trunc((Menu.TOUCH_MIN - size) / 2)));
  }

  private hit(mx: number, my: number, x: number, y: number, w: number, h: number): boolean {
    const sx = Menu.touchSlop(w);
    const sy = Menu.touchSlop(h);
    return mx >= x - sx && mx <= x + w + sx && my >= y - sy && my <= y + h + sy;
  }

  private hitBack(): boolean {
    return this.hit(this.mouseX, this.mouseY, PageRenderer.backX(), PageRenderer.BACK_Y, PageRenderer.BACK_W, PageRenderer.BACK_H);
  }

  private resetHover(): void {
    for (let i = 0; i < this.btn.length; i++) this.btn[i] = 0;
    for (let i = 0; i < 4; i++) this.pauseBtn[i] = 0;
    this.backH = this.musicH = this.shopH = this.dailyH = this.aboutH = this.changelogH = this.creditsLinkH = this.quitH = this.retryH = 0;
    this.helpScroll = this.helpScrollTarget = 0;
    this.settingsScroll = this.settingsScrollTarget = 0;
    for (let i = 0; i < Menu.SET_HOVER_COUNT; i++) this.setH[i] = 0;
    this.setMusicBarH = this.setSfxBarH = 0;
    this.settingsDrag = SettingsDrag.NONE;
    this.confirmAction = null;
    this.confirmYesH = this.confirmNoH = 0;
  }

  // Pending game start — set by Select, consumed by Loadout
  private pendingDifficulty = -1;

  private startGame(difficulty: number, firstEnemy: GameObject): void {
    this.handler.getObjects().length = 0;
    this.hud.setLevel(1);
    this.hud.setScore(0);
    this.hud.setPoints(0);
    this.hud.bounds = 0;
    this.hud.resetStats();

    // Apply perk + coin shop bonuses to starting stats
    const healthBonus = Perks.getStartingHealthBonus() + CoinShop.getPermHealthBonus();
    const healthMult = Perks.getMaxHealthMultiplier();
    const baseHealth = (100 + healthBonus) * healthMult;
    HUD.HEALTH = baseHealth;
    this.hud.bounds = Math.trunc((baseHealth - 100) * 2);

    this.handler.spd = 6 + Perks.getStartingSpeedBonus() + CoinShop.getPermSpeedBonus();
    this.game.shop.reset();
    Game.setTimeScale(1);
    // Combat mode uses Hard-level palette but its own diff
    GamePalette.setDifficulty(Game.combatMode ? 1 : difficulty);
    GamePalette.reset();
    Profile.startRun(Game.combatMode ? 1 : difficulty);
    Game.currentAttempt = Profile.getCurrentAttempt();
    this.game.resetRunTracking();
    Game.attemptFade = 1;
    Game.gameState = STATE.Game;
    this.handler.addObject(new Player(Math.trunc(Game.WIDTH / 2) - 32, Math.trunc(Game.HEIGHT / 2) - 32, ID.Player, this.handler));
    this.handler.addObject(firstEnemy);
    this.game.diff = difficulty;
    this.hud.triggerWaveAnnounce();
  }

  private startDailyGame(): void {
    if (!DailyChallenge.canPlay()) return;

    const difficulty = DailyChallenge.todayDifficulty();
    this.handler.getObjects().length = 0;
    this.hud.setLevel(1);
    this.hud.setScore(0);
    this.hud.setPoints(0);
    this.hud.bounds = 0;
    this.hud.resetStats();

    // Daily runs: no perk/shop bonuses — pure skill, same conditions for everyone
    Perks.clearLoadout();
    HUD.HEALTH = 100;
    this.hud.bounds = 0;
    this.handler.spd = 6;
    this.game.shop.reset();
    Game.setTimeScale(1);
    GamePalette.setDifficulty(difficulty);
    GamePalette.reset();
    Profile.startRun(difficulty);
    Game.currentAttempt = Profile.getCurrentAttempt();
    this.game.resetRunTracking();

    // Seed the spawner for deterministic enemy patterns
    Game.dailyMode = true;
    Game.seedSpawner(DailyChallenge.todaySeed());

    Game.attemptFade = 1;
    Game.gameState = STATE.Game;
    this.handler.addObject(new Player(Math.trunc(Game.WIDTH / 2) - 32, Math.trunc(Game.HEIGHT / 2) - 32, ID.Player, this.handler));

    // First enemy is also seeded (same for everyone)
    const seedRng = new Random(DailyChallenge.todaySeed());
    let firstEnemy: GameObject;
    if (difficulty === 0) {
      firstEnemy = new BasicEnemy(seedRng.nextInt(Game.WIDTH - 80) + 20,
        seedRng.nextInt(Game.HEIGHT - 80) + 20, ID.BasicEnemy, this.handler);
    } else {
      firstEnemy = new HardEnemy(seedRng.nextInt(Game.WIDTH - 80) + 20,
        seedRng.nextInt(Game.HEIGHT - 80) + 20, ID.BasicEnemy, this.handler);
    }
    this.handler.addObject(firstEnemy);
    this.game.diff = difficulty;
    this.hud.triggerWaveAnnounce();
  }

  private resetForMenu(): void {
    this.handler.getObjects().length = 0;
    this.hud.setLevel(1);
    this.hud.setScore(0);
    this.hud.setPoints(0);
    this.hud.bounds = 0;
    HUD.HEALTH = 100;
    Game.setTimeScale(1);
    Game.dailyMode = false;
    Game.combatMode = false;
    Game.mouseShootHeld = false;
    this.resetHover();
  }

  // ==================== Rendering ====================

  public render(g: Graphics2D): void {
    const g2: Graphics2D = g;
    g2.setRenderingHint(RenderingHints.KEY_TEXT_ANTIALIASING, RenderingHints.VALUE_TEXT_ANTIALIAS_ON);
    g2.setRenderingHint(RenderingHints.KEY_ANTIALIASING, RenderingHints.VALUE_ANTIALIAS_ON);

    switch (Game.gameState) {
      case STATE.Menu:          this.renderMainMenu(g2); break;
      case STATE.Profile:       this.renderProfile(g2); break;
      case STATE.Select:        this.renderSelect(g2); break;
      case STATE.Help:          this.renderHelp(g2); break;
      case STATE.HelpENG:       this.renderHelpPage(g2, 'English', [
        ['How to Play', 'Navigate your character through the arena while avoiding all enemies. Survive as long as possible to increase your score. Each level introduces new and tougher enemies. Boss battles occur at milestone levels. Earn points over time to spend on upgrades in the shop.'],
        ['Controls', 'W / ↑  -  Move up\nA / ←  -  Move left\nS / ↓  -  Move down\nD / →  -  Move right\n\nShift  -  Dash (short burst with invincibility)\nE  -  Slow Motion (slows everything for 2.5s)\nSpace  -  Open shop\nP / Esc  -  Pause menu\n\nShield activates automatically and absorbs one hit.'],
        ['Enemy Types', 'Red squares bounce in straight lines. Teal diamonds move at high speed. Purple circles track and follow the player. Yellow triangles change direction randomly. Red bosses are large and spawn bullets.'],
        ['Tips', 'Keep moving — standing still is the fastest way to lose health. Upgrade speed early for better survivability. Save points for health upgrades at higher levels. Each enemy type has a unique shape and behavior. Learn their patterns to dodge effectively.\n\nBefore each run you can choose up to 2 perks from the Loadout screen. Perks unlock as you level up.'],
      ]); break;
      case STATE.HelpNLD:       this.renderHelpPage(g2, 'Nederlands', [
        ['Hoe te spelen', 'Navigeer je karakter door de arena en ontwijk alle vijanden. Overleef zo lang mogelijk om je score te verhogen. Elk level introduceert nieuwe en sterkere vijanden. Baasgevechten vinden plaats bij mijlpaalniveaus. Verdien punten om upgrades te kopen in de winkel.'],
        ['Besturing', 'W / ↑  -  Omhoog\nA / ←  -  Links\nS / ↓  -  Omlaag\nD / →  -  Rechts\n\nSpatie  -  Winkel openen\nP / Esc  -  Pauzemenu'],
        ['Vijandtypes', 'Rode vierkanten stuiteren in rechte lijnen. Turquoise ruiten bewegen met hoge snelheid. Paarse cirkels volgen de speler. Gele driehoeken veranderen willekeurig van richting. Rode bazen zijn groot en schieten kogels.'],
        ['Tips', 'Blijf bewegen — stilstaan is de snelste manier om gezondheid te verliezen. Upgrade snelheid vroeg voor betere overleving. Spaar punten voor gezondheid bij hogere levels. Elk vijandtype heeft een unieke vorm en gedrag. Leer hun patronen om effectief te ontwijken.'],
      ]); break;
      case STATE.HelpDEU:       this.renderHelpPage(g2, 'Deutsch', [
        ['Spielanleitung', 'Navigiere deinen Charakter durch die Arena und weiche allen Feinden aus. Überlebe so lange wie möglich, um deinen Score zu erhöhen. Jedes Level bringt neue, stärkere Feinde. Bosskämpfe finden bei Meilensteinleveln statt. Verdiene Punkte für Upgrades im Shop.'],
        ['Steuerung', 'W / ↑  -  Nach oben\nA / ←  -  Links\nS / ↓  -  Nach unten\nD / →  -  Rechts\n\nLeertaste  -  Shop öffnen\nP / Esc  -  Pausenmenü'],
        ['Gegnertypen', 'Rote Quadrate prallen in geraden Linien ab. Türkise Rauten bewegen sich mit hoher Geschwindigkeit. Lila Kreise verfolgen den Spieler. Gelbe Dreiecke ändern zufällig die Richtung. Rote Bosse sind groß und schießen Kugeln.'],
        ['Tipps', 'Bleib in Bewegung — Stillstehen kostet am schnellsten Gesundheit. Upgrade Geschwindigkeit früh für besseres Überleben. Spare Punkte für höhere Level. Jeder Feindtyp hat eine eigene Form und Verhaltensweise. Lerne ihre Muster.'],
      ]); break;
      case STATE.Settings:      this.renderSettings(g2); break;
      case STATE.Statistics:    this.renderStatistics(g2); break;
      case STATE.AchievementsPage: this.renderAchievements(g2); break;
      case STATE.Customize:     this.renderCustomize(g2); break;
      case STATE.Loadout:       this.renderLoadout(g2); break;
      case STATE.CoinShopPage:  this.renderCoinShop(g2); break;
      case STATE.DailyPage:     this.renderDailyPage(g2); break;
      case STATE.About:         this.renderAbout(g2); break;
      case STATE.Update_Notes:  this.renderUpdates(g2); break;
      case STATE.Credits:       this.renderCredits(g2); break;
      case STATE.End:           this.renderEnd(g2); break;
      case STATE.Paused:        this.renderPauseMenu(g2); break;
      default: break;
    }
  }

  // ---------- Main Menu ----------

  private renderMainMenu(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawLogo(g, 180);

    // Level + XP — top center
    const lvlCx = Math.trunc(Game.WIDTH / 2);
    g.setFont(PageRenderer.LABEL_FONT);
    g.setColor(PageRenderer.ACCENT);
    const lvlLabel = 'LEVEL ' + Profile.getLevel();
    const lfm: FontMetrics = g.getFontMetrics();
    g.drawString(lvlLabel, lvlCx - Math.trunc(lfm.stringWidth(lvlLabel) / 2), 34);
    const xpBarW = 120;
    const xpBarH = 3;
    const xpBarX = lvlCx - Math.trunc(xpBarW / 2);
    g.setColor(PageRenderer.SURFACE);
    g.fillRoundRect(xpBarX, 40, xpBarW, xpBarH, 2, 2);
    const xpFillW = Math.trunc(xpBarW * Profile.levelProgress());
    if (xpFillW > 0) {
      g.setColor(PageRenderer.ACCENT);
      g.fillRoundRect(xpBarX, 40, xpFillW, xpBarH, 2, 2);
    }

    // 5 centered buttons
    const bx = Menu.btnX();
    PageRenderer.drawPrimaryButton(g, bx, Menu.PLAY_Y, Menu.BW, Menu.BH, 'Play', this.btn[0]);
    PageRenderer.drawSecondaryButton(g, bx, Menu.PROFILE_MENU_Y, Menu.BW, Menu.BH, 'Profile', this.btn[1]);
    PageRenderer.drawSecondaryButton(g, bx, Menu.SHOP_MENU_Y, Menu.BW, Menu.BH, 'Shop', this.btn[2]);
    PageRenderer.drawSecondaryButton(g, bx, Menu.SETTINGS_MENU_Y, Menu.BW, Menu.BH, 'Settings', this.btn[3]);
    PageRenderer.drawSecondaryButton(g, bx, Menu.HELP_MENU_Y, Menu.BW, Menu.BH, 'Help', this.btn[4]);

    // Quit icon (top-right) — X button
    const qx = Menu.quitIconX();
    g.setColor(PageRenderer.lerp(PageRenderer.SURFACE, new Color(60, 30, 30), this.quitH));
    g.fillRoundRect(qx, Menu.QUIT_Y, Menu.QUIT_SIZE, Menu.QUIT_SIZE, 8, 8);
    g.setColor(PageRenderer.lerp(PageRenderer.BORDER, PageRenderer.DANGER, this.quitH * 0.6));
    g.drawRoundRect(qx, Menu.QUIT_Y, Menu.QUIT_SIZE, Menu.QUIT_SIZE, 8, 8);
    g.setColor(PageRenderer.lerp(PageRenderer.TEXT_SEC, PageRenderer.DANGER, this.quitH));
    const oldStroke: BasicStroke = g.getStroke();
    g.setStroke(new BasicStroke(2, BasicStroke.CAP_ROUND, BasicStroke.JOIN_ROUND));
    const xc = qx + Math.trunc(Menu.QUIT_SIZE / 2), yc = Menu.QUIT_Y + Math.trunc(Menu.QUIT_SIZE / 2), xr = 6;
    g.drawLine(xc - xr, yc - xr, xc + xr, yc + xr);
    g.drawLine(xc + xr, yc - xr, xc - xr, yc + xr);
    g.setStroke(oldStroke);

    // Top-left: Now playing + music icon
    if (AudioPlayer.getTrackCount() > 0) {
      // Music icon
      g.setColor(PageRenderer.lerp(PageRenderer.SURFACE, new Color(25, 45, 55), this.musicH));
      g.fillRoundRect(24, 20, Menu.MUSIC_ICON_SIZE, Menu.MUSIC_ICON_SIZE, 6, 6);
      g.setColor(PageRenderer.lerp(PageRenderer.BORDER, PageRenderer.ACCENT, this.musicH * 0.5));
      g.drawRoundRect(24, 20, Menu.MUSIC_ICON_SIZE, Menu.MUSIC_ICON_SIZE, 6, 6);
      g.setFont(PageRenderer.SMALL_FONT);
      g.setColor(PageRenderer.lerp(PageRenderer.TEXT_SEC, PageRenderer.ACCENT, this.musicH));
      PageRenderer.drawCenteredString(g, '♫', 24, 20, Menu.MUSIC_ICON_SIZE, Menu.MUSIC_ICON_SIZE);
      // Now playing text
      g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('Now playing', 24 + Menu.MUSIC_ICON_SIZE + 8, 28);
      g.setColor(PageRenderer.TEXT_SEC);
      g.drawString(AudioPlayer.getCurrentTrack()!.displayName, 24 + Menu.MUSIC_ICON_SIZE + 8, 44);
    }

    // Bottom-right: Daily streak
    const canPlayDaily = DailyChallenge.canPlay();
    const dailyCol = canPlayDaily ? new Color(120, 200, 255) : PageRenderer.TEXT_MUTED;
    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(PageRenderer.lerp(canPlayDaily ? new Color(140, 190, 230) : PageRenderer.TEXT_MUTED,
      dailyCol, this.dailyH));
    const dailyLabel = canPlayDaily ? 'Daily  ·  ' + DailyChallenge.getCurrentStreak() + ' streak'
      : 'Daily  ·  Done';
    const dfm: FontMetrics = g.getFontMetrics();
    g.drawString(dailyLabel, Game.WIDTH - dfm.stringWidth(dailyLabel) - 24, 700);

    // Version — bottom-left
    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    g.drawString('v4.1', 24, 700);

    // About | Changelog | Credits — centered bottom
    this.drawBottomLinks(g);
  }

  // ---------- Profile ----------

  private renderProfile(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Profile');
    PageRenderer.drawBackButton(g, this.backH);

    // Level + XP
    const cx = Math.trunc(Game.WIDTH / 2);
    g.setFont(PageRenderer.LABEL_FONT);
    g.setColor(PageRenderer.ACCENT);
    const lvlLabel = 'LEVEL ' + Profile.getLevel();
    const fm: FontMetrics = g.getFontMetrics();
    g.drawString(lvlLabel, cx - Math.trunc(fm.stringWidth(lvlLabel) / 2), 140);
    const xpBarW = 160;
    const xpBarH = 4;
    const xpBarX = cx - Math.trunc(xpBarW / 2);
    g.setColor(PageRenderer.SURFACE);
    g.fillRoundRect(xpBarX, 150, xpBarW, xpBarH, 2, 2);
    const xpFillW = Math.trunc(xpBarW * Profile.levelProgress());
    if (xpFillW > 0) {
      g.setColor(PageRenderer.ACCENT);
      g.fillRoundRect(xpBarX, 150, xpFillW, xpBarH, 2, 2);
    }

    // 3 buttons
    const bx = Menu.btnX();
    PageRenderer.drawSecondaryButton(g, bx, 200, Menu.BW, Menu.BH, 'Customize', this.btn[0]);
    PageRenderer.drawSecondaryButton(g, bx, 258, Menu.BW, Menu.BH, 'Statistics', this.btn[1]);
    PageRenderer.drawSecondaryButton(g, bx, 316, Menu.BW, Menu.BH,
      'Achievements  ' + Achievements.getUnlockedCount() + '/' + Achievements.getCount(), this.btn[2]);
  }

  // ---------- Select Difficulty ----------

  private renderSelect(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Select Mode');
    PageRenderer.drawBackButton(g, this.backH);

    g.setFont(PageRenderer.SUBTITLE_FONT);
    g.setColor(PageRenderer.TEXT_SEC);
    const sub = 'Choose your challenge';
    const fm: FontMetrics = g.getFontMetrics();
    g.drawString(sub, Math.trunc((Game.WIDTH - fm.stringWidth(sub)) / 2), 130);

    const bx = Menu.btnX();
    PageRenderer.drawSecondaryButton(g, bx, Menu.PLAY_Y, Menu.BW, Menu.BH, 'Normal', this.btn[0]);
    PageRenderer.drawWarningButton(g, bx, Menu.INFO_Y, Menu.BW, Menu.BH, 'Hard', this.btn[1]);
    PageRenderer.drawDangerButton(g, bx, Menu.HELP_Y, Menu.BW, Menu.BH, 'Insane', this.btn[2]);
    PageRenderer.drawPrimaryButton(g, bx, Menu.COMBAT_Y, Menu.BW, Menu.BH, 'Combat', this.btn[3]);

    g.setFont(PageRenderer.SMALL_FONT);
    const labelX = bx + Menu.BW + 20;
    g.setColor(PageRenderer.lerp(PageRenderer.TEXT_MUTED, PageRenderer.TEXT_SEC, this.btn[0]));
    g.drawString('Recommended for beginners', labelX, Menu.PLAY_Y + 30);
    g.setColor(PageRenderer.lerp(new Color(245, 195, 68, 120), new Color(255, 220, 100), this.btn[1]));
    g.drawString('Unpredictable enemies', labelX, Menu.INFO_Y + 30);
    g.setColor(PageRenderer.lerp(new Color(235, 87, 87, 120), new Color(255, 120, 120), this.btn[2]));
    g.drawString('Only for the brave', labelX, Menu.HELP_Y + 30);
    g.setColor(PageRenderer.lerp(PageRenderer.ACCENT, new Color(120, 240, 230), this.btn[3]));
    g.drawString('Shoot, parry & dash-strike', labelX, Menu.COMBAT_Y + 30);
  }

  // ---------- Help ----------

  private renderHelp(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Help');
    PageRenderer.drawBackButton(g, this.backH);

    g.setFont(PageRenderer.SUBTITLE_FONT);
    g.setColor(PageRenderer.TEXT_SEC);
    const sub = 'Select your language';
    const fm: FontMetrics = g.getFontMetrics();
    g.drawString(sub, Math.trunc((Game.WIDTH - fm.stringWidth(sub)) / 2), 130);

    const lx = Menu.langX();
    PageRenderer.drawSecondaryButton(g, lx, Menu.PLAY_Y, Menu.LANG_W, Menu.BH, 'English', this.btn[0]);
    PageRenderer.drawSecondaryButton(g, lx, Menu.INFO_Y, Menu.LANG_W, Menu.BH, 'Nederlands', this.btn[1]);
    PageRenderer.drawSecondaryButton(g, lx, Menu.HELP_Y, Menu.LANG_W, Menu.BH, 'Deutsch', this.btn[2]);
  }

  // ---------- Help Language ----------

  private renderHelpPage(g: Graphics2D, language: string, sections: string[][]): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Help');
    PageRenderer.drawBackButton(g, this.backH);

    // Language badge
    g.setFont(PageRenderer.LABEL_FONT);
    g.setColor(PageRenderer.ACCENT);
    const badgeFm: FontMetrics = g.getFontMetrics();
    const badge = language.toUpperCase();
    const bw = badgeFm.stringWidth(badge) + 20;
    const bxp = Math.trunc((Game.WIDTH - bw) / 2);
    g.drawRoundRect(bxp, 105, bw, 24, 6, 6);
    g.drawString(badge, bxp + 10, 122);

    // Content area
    const margin = 60;
    const panelW = Game.WIDTH - margin * 2;
    const contentTop = 145;
    const contentBottom = Game.HEIGHT - 30;
    const gap = 12;
    const padding = 20;
    const lineH = 22;

    // Clip to content area
    const oldClip: Rectangle | null = g.getClip();
    g.clipRect(0, contentTop, Game.WIDTH, contentBottom - contentTop);

    // Calculate total content height for scroll clamping
    g.setFont(PageRenderer.BODY_FONT);
    const bodyFm: FontMetrics = g.getFontMetrics();
    let totalHeight = 0;
    for (const section of sections) {
      const sectionContentH = this.measureSectionHeight(section[1], panelW - padding * 2, bodyFm, lineH);
      totalHeight += 52 + sectionContentH + padding + gap;
    }
    const maxScroll = Math.max(0, totalHeight - (contentBottom - contentTop) + 20);
    this.helpScrollTarget = Math.min(this.helpScrollTarget, maxScroll);

    let y = contentTop - Math.trunc(this.helpScroll);

    for (const section of sections) {
      const title = section[0];
      const text = section[1];

      // Measure this section
      const sectionContentH = this.measureSectionHeight(text, panelW - padding * 2, bodyFm, lineH);
      const panelH = 52 + sectionContentH + padding;

      // Only draw if visible
      if (y + panelH > contentTop - 20 && y < contentBottom + 20) {
        PageRenderer.drawPanel(g, margin, y, panelW, panelH);

        g.setFont(PageRenderer.HEADING_FONT);
        g.setColor(PageRenderer.ACCENT);
        g.drawString(title, margin + padding, y + 34);

        g.setColor(PageRenderer.BORDER);
        g.fillRect(margin + padding, y + 46, panelW - padding * 2, 1);

        g.setFont(PageRenderer.BODY_FONT);
        g.setColor(PageRenderer.TEXT_SEC);
        this.drawWrappedText(g, text, margin + padding, y + 68, panelW - padding * 2, lineH);
      }

      y += panelH + gap;
    }

    g.setClip(oldClip);

    // Scroll indicator if content overflows
    if (maxScroll > 0) {
      const scrollPct = (this.helpScrollTarget > 0) ? this.helpScroll / maxScroll : 0;
      const trackH = contentBottom - contentTop - 20;
      const thumbH = Math.max(30, Math.trunc((contentBottom - contentTop) / totalHeight * trackH));
      const thumbY = contentTop + 10 + Math.trunc((trackH - thumbH) * scrollPct);
      g.setColor(new Color(40, 52, 70, 80));
      g.fillRoundRect(Game.WIDTH - 22, contentTop + 10, 6, trackH, 3, 3);
      g.setColor(new Color(78, 205, 196, 120));
      g.fillRoundRect(Game.WIDTH - 22, thumbY, 6, thumbH, 3, 3);
    }
  }

  private measureSectionHeight(text: string, maxW: number, fm: FontMetrics, lineH: number): number {
    const parts = text.split('\n');
    let lines = 0;
    for (const part of parts) {
      if (part.length === 0) {
        lines++;
      } else {
        lines += this.wrapLineCount(part, maxW, fm);
      }
    }
    return lines * lineH;
  }

  private wrapLineCount(text: string, maxW: number, fm: FontMetrics): number {
    const words = text.split(' ');
    let count = 1;
    let lineW = 0;
    for (const word of words) {
      const wordW = fm.stringWidth(word + ' ');
      if (lineW + wordW > maxW && lineW > 0) {
        count++;
        lineW = 0;
      }
      lineW += wordW;
    }
    return count;
  }

  private drawWrappedText(g: Graphics2D, text: string, x: number, y: number, maxW: number, lineH: number): void {
    const fm: FontMetrics = g.getFontMetrics();
    const parts = text.split('\n');
    let cy = y;
    for (const part of parts) {
      if (part.length === 0) {
        cy += lineH;
        continue;
      }
      const words = part.split(' ');
      let line = '';
      for (const word of words) {
        const test = line.length === 0 ? word : line + ' ' + word;
        if (fm.stringWidth(test) > maxW && line.length > 0) {
          g.drawString(line, x, cy);
          cy += lineH;
          line = word;
        } else {
          line = test;
        }
      }
      if (line.length > 0) {
        g.drawString(line, x, cy);
        cy += lineH;
      }
    }
  }

  // ---------- Settings ----------

  // Layout constants for settings page
  private static readonly SET_MARGIN = 60;
  private static readonly SET_PAD = 20;
  private static readonly SET_ROW_H = 42;
  private static readonly SET_SLIDER_W = 200;
  private static readonly SET_SLIDER_H = 5;
  private static readonly SET_SLIDER_KNOB = 12;
  private static readonly SET_DRAG_MARGIN = 16;
  private static readonly SET_OPT_W = 80;
  private static readonly SET_OPT_H = 32;
  private static readonly SET_OPT_GAP = 8;
  private static readonly SET_TOGGLE_W = 50;
  private static readonly SET_TOGGLE_H = 26;
  private static readonly SET_LANG_W = 120;
  private static readonly SET_LANG_H = 34;
  private static readonly SET_LANG_GAP = 10;
  private static readonly SET_DANGER_W = 260;
  private static readonly SET_DANGER_H = 40;

  private static setPanelW(): number { return Game.WIDTH - Menu.SET_MARGIN * 2; }
  private static setLabelX(): number { return Menu.SET_MARGIN + Menu.SET_PAD; }
  private static setControlX(): number { return Menu.SET_MARGIN + Math.trunc(Menu.setPanelW() / 2) + 20; }

  private settingsSliderRatio(mx: number): number {
    return Math.max(0, Math.min(1, (mx - Menu.setControlX()) / Menu.SET_SLIDER_W));
  }

  private hitSlider(mx: number, my: number, sliderY: number): boolean {
    return mx >= Menu.setControlX() - Menu.SET_DRAG_MARGIN && mx <= Menu.setControlX() + Menu.SET_SLIDER_W + Menu.SET_DRAG_MARGIN
      && my >= sliderY - Menu.SET_DRAG_MARGIN && my <= sliderY + Menu.SET_SLIDER_H + Menu.SET_DRAG_MARGIN;
  }

  private updateSettingsHover(): void {
    // Slider bar hovers
    const onMusic = this.settingsDrag === SettingsDrag.MUSIC_VOL
      || this.hitSlider(this.mouseX, this.mouseY, this.getSettingsRowY(0));
    this.setMusicBarH += ((onMusic ? 1 : 0) - this.setMusicBarH) * Menu.LERP;

    // Option button hovers
    const targets = this.getSettingsHoverTargets();
    for (let i = 0; i < Menu.SET_HOVER_COUNT; i++) {
      this.setH[i] = this.approach(this.setH[i], targets[i]);
    }

    // Confirm dialog hovers
    if (this.confirmAction !== null) {
      const cx = Math.trunc(Game.WIDTH / 2);
      const dy = Math.trunc(Game.HEIGHT / 2);
      this.confirmYesH = this.approach(this.confirmYesH, this.hit(this.mouseX, this.mouseY, cx - 130, dy + 30, 120, 38));
      this.confirmNoH = this.approach(this.confirmNoH, this.hit(this.mouseX, this.mouseY, cx + 10, dy + 30, 120, 38));
    }
  }

  private getSettingsRowY(row: number): number {
    // Returns approximate Y for a settings row, adjusted by scroll
    // Row 0-1: Audio (panel starts at y=130)
    // Row 2: Screen shake (panel starts ~260)
    // etc. — computed dynamically during render, so for hover we compute it here too
    const contentTop = 130;
    let y = contentTop - Math.trunc(this.settingsScroll);
    const gap = 12;

    // Audio panel: row 0 (music only)
    if (row <= 0) return y + 52 + row * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2);

    // Visual panel: starts after audio
    const audioH = 52 + 1 * Menu.SET_ROW_H + Menu.SET_PAD;
    y += audioH + gap;
    if (row <= 7) return y + 52 + (row - 2) * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2);

    // Controls panel (general panel removed)
    const visualH = 52 + 6 * Menu.SET_ROW_H + Menu.SET_PAD;
    y += visualH + gap;
    if (row <= 10) return y + 52 + (row - 10) * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2);

    // Danger panel
    const controlsH = 52 + 7 * 28 + Menu.SET_PAD;
    y += controlsH + gap;
    return y + 52 + (row - 11) * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2);
  }

  private getSettingsHoverTargets(): boolean[] {
    const t: boolean[] = new Array<boolean>(Menu.SET_HOVER_COUNT).fill(false);
    if (this.confirmAction !== null) return t; // Don't hover underneath dialog

    const contentTop = 130;
    const contentBottom = Game.HEIGHT - 30;
    const gap = 12;
    const cx = Menu.setControlX();
    let y = contentTop - Math.trunc(this.settingsScroll);

    // --- Audio panel (music only) ---
    const audioH = 52 + 1 * Menu.SET_ROW_H + Menu.SET_PAD;
    y += audioH + gap;

    // --- Visual panel ---
    let rowY: number;
    // Screen shake (row 0 in visual)
    rowY = y + 52 + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_OPT_H / 2);
    for (let i = 0; i < 3; i++) {
      const ox = cx + i * (Menu.SET_OPT_W + Menu.SET_OPT_GAP);
      t[2 + i] = this.hit(this.mouseX, this.mouseY, ox, rowY, Menu.SET_OPT_W, Menu.SET_OPT_H)
        && this.mouseY > contentTop && this.mouseY < contentBottom;
    }

    // Particle density (row 1 in visual)
    rowY = y + 52 + Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_OPT_H / 2);
    for (let i = 0; i < 3; i++) {
      const ox = cx + i * (Menu.SET_OPT_W + Menu.SET_OPT_GAP);
      t[5 + i] = this.hit(this.mouseX, this.mouseY, ox, rowY, Menu.SET_OPT_W, Menu.SET_OPT_H)
        && this.mouseY > contentTop && this.mouseY < contentBottom;
    }

    // FPS toggle (row 2)
    rowY = y + 52 + 2 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_TOGGLE_H / 2);
    t[8] = this.hit(this.mouseX, this.mouseY, cx, rowY, Menu.SET_TOGGLE_W, Menu.SET_TOGGLE_H)
      && this.mouseY > contentTop && this.mouseY < contentBottom;

    // Trail toggle (row 3)
    rowY = y + 52 + 3 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_TOGGLE_H / 2);
    t[9] = this.hit(this.mouseX, this.mouseY, cx, rowY, Menu.SET_TOGGLE_W, Menu.SET_TOGGLE_H)
      && this.mouseY > contentTop && this.mouseY < contentBottom;

    // Grid toggle (row 4)
    rowY = y + 52 + 4 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_TOGGLE_H / 2);
    t[10] = this.hit(this.mouseX, this.mouseY, cx, rowY, Menu.SET_TOGGLE_W, Menu.SET_TOGGLE_H)
      && this.mouseY > contentTop && this.mouseY < contentBottom;

    // Colorblind toggle (row 5)
    rowY = y + 52 + 5 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_TOGGLE_H / 2);
    t[11] = this.hit(this.mouseX, this.mouseY, cx, rowY, Menu.SET_TOGGLE_W, Menu.SET_TOGGLE_H)
      && this.mouseY > contentTop && this.mouseY < contentBottom;

    const visualH = 52 + 6 * Menu.SET_ROW_H + Menu.SET_PAD;
    y += visualH + gap;

    // --- Controls panel (read-only) ---
    const controlsH = 52 + 7 * 28 + Menu.SET_PAD;
    y += controlsH + gap;

    // --- Danger zone ---
    rowY = y + 52 + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_DANGER_H / 2);
    const dangerCx = Math.trunc((Game.WIDTH - (Menu.SET_DANGER_W * 2 + 20)) / 2);
    t[18] = this.hit(this.mouseX, this.mouseY, dangerCx, rowY, Menu.SET_DANGER_W, Menu.SET_DANGER_H)
      && this.mouseY > contentTop && this.mouseY < contentBottom;
    t[19] = this.hit(this.mouseX, this.mouseY, dangerCx + Menu.SET_DANGER_W + 20, rowY, Menu.SET_DANGER_W, Menu.SET_DANGER_H)
      && this.mouseY > contentTop && this.mouseY < contentBottom;

    return t;
  }

  private handleSettingsClick(mx: number, my: number): void {
    // Confirmation dialog takes priority
    if (this.confirmAction !== null) {
      const cx = Math.trunc(Game.WIDTH / 2);
      const dy = Math.trunc(Game.HEIGHT / 2);
      if (this.hit(mx, my, cx - 130, dy + 30, 120, 38)) {
        // Confirm yes
        if (this.confirmAction === 'scores') Profile.resetHighScores();
        else if (this.confirmAction === 'progress') { Profile.resetAll(); Settings.resetToDefaults(); Achievements.resetAll(); CoinShop.resetAll(); DailyChallenge.resetAll(); }
        this.confirmAction = null;
      } else if (this.hit(mx, my, cx + 10, dy + 30, 120, 38)) {
        this.confirmAction = null; // Cancel
      }
      return;
    }

    // Back
    if (this.hitBack()) {
      Game.gameState = (Menu.settingsReturnTo !== null) ? Menu.settingsReturnTo : STATE.Menu;
      Menu.settingsReturnTo = null;
      this.resetHover(); return;
    }

    const contentTop = 130;
    const contentBottom = Game.HEIGHT - 30;
    if (my < contentTop || my > contentBottom) return;

    const gap = 12;
    const cx2 = Menu.setControlX();
    let y = contentTop - Math.trunc(this.settingsScroll);

    // --- Audio: music slider only ---
    const audioH = 52 + 1 * Menu.SET_ROW_H + Menu.SET_PAD;
    const sliderY0 = y + 52 + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_SLIDER_H / 2);

    if (this.hitSlider(mx, my, sliderY0)) {
      this.settingsDrag = SettingsDrag.MUSIC_VOL;
      Settings.setMusicVolume(this.settingsSliderRatio(mx));
      return;
    }

    y += audioH + gap;

    // --- Visual: options & toggles ---
    let rowY: number;

    // Screen shake
    rowY = y + 52 + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_OPT_H / 2);
    for (let i = 0; i < 3; i++) {
      const ox = cx2 + i * (Menu.SET_OPT_W + Menu.SET_OPT_GAP);
      if (this.hit(mx, my, ox, rowY, Menu.SET_OPT_W, Menu.SET_OPT_H)) {
        Settings.setScreenShake(i); return;
      }
    }

    // Particle density
    rowY = y + 52 + Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_OPT_H / 2);
    for (let i = 0; i < 3; i++) {
      const ox = cx2 + i * (Menu.SET_OPT_W + Menu.SET_OPT_GAP);
      if (this.hit(mx, my, ox, rowY, Menu.SET_OPT_W, Menu.SET_OPT_H)) {
        Settings.setParticleDensity(i); return;
      }
    }

    // FPS toggle
    rowY = y + 52 + 2 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_TOGGLE_H / 2);
    if (this.hit(mx, my, cx2, rowY, Menu.SET_TOGGLE_W, Menu.SET_TOGGLE_H)) {
      Settings.setShowFps(!Settings.getShowFps()); return;
    }

    // Trail toggle
    rowY = y + 52 + 3 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_TOGGLE_H / 2);
    if (this.hit(mx, my, cx2, rowY, Menu.SET_TOGGLE_W, Menu.SET_TOGGLE_H)) {
      Settings.setPlayerTrail(!Settings.getPlayerTrail()); return;
    }

    // Grid toggle
    rowY = y + 52 + 4 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_TOGGLE_H / 2);
    if (this.hit(mx, my, cx2, rowY, Menu.SET_TOGGLE_W, Menu.SET_TOGGLE_H)) {
      Settings.setGridDots(!Settings.getGridDots()); return;
    }

    // Colorblind toggle
    rowY = y + 52 + 5 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_TOGGLE_H / 2);
    if (this.hit(mx, my, cx2, rowY, Menu.SET_TOGGLE_W, Menu.SET_TOGGLE_H)) {
      Settings.setColorblindMode(!Settings.getColorblindMode()); return;
    }

    const visualH = 52 + 6 * Menu.SET_ROW_H + Menu.SET_PAD;
    y += visualH + gap;

    // --- Controls (read-only) ---
    const controlsH = 52 + 7 * 28 + Menu.SET_PAD;
    y += controlsH + gap;

    // --- Danger zone ---
    rowY = y + 52 + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_DANGER_H / 2);
    const dangerCx = Math.trunc((Game.WIDTH - (Menu.SET_DANGER_W * 2 + 20)) / 2);
    if (this.hit(mx, my, dangerCx, rowY, Menu.SET_DANGER_W, Menu.SET_DANGER_H)) {
      this.confirmAction = 'scores'; return;
    }
    if (this.hit(mx, my, dangerCx + Menu.SET_DANGER_W + 20, rowY, Menu.SET_DANGER_W, Menu.SET_DANGER_H)) {
      this.confirmAction = 'progress'; return;
    }
  }

  private renderSettings(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Settings');
    PageRenderer.drawBackButton(g, this.backH);

    const contentTop = 130;
    const contentBottom = Game.HEIGHT - 30;
    const pw = Menu.setPanelW();
    const gap = 12;
    const cx = Menu.setControlX();
    const lx = Menu.setLabelX();

    // Clip to content area
    const oldClip: Rectangle | null = g.getClip();
    g.clipRect(0, contentTop, Game.WIDTH, contentBottom - contentTop);

    let y = contentTop - Math.trunc(this.settingsScroll);

    // ===== AUDIO PANEL =====
    const audioH = 52 + 1 * Menu.SET_ROW_H + Menu.SET_PAD;
    if (y + audioH > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, Menu.SET_MARGIN, y, pw, audioH);
      g.setFont(PageRenderer.HEADING_FONT);
      g.setColor(PageRenderer.ACCENT);
      g.drawString('Audio', lx, y + 34);
      g.setColor(PageRenderer.BORDER);
      g.fillRect(lx, y + 46, pw - Menu.SET_PAD * 2, 1);

      // Music volume
      const rowY = y + 52 + Math.trunc(Menu.SET_ROW_H / 2);
      g.setFont(PageRenderer.BODY_FONT);
      g.setColor(PageRenderer.TEXT_SEC);
      g.drawString('Music Volume', lx, rowY + 5);
      this.renderSlider(g, cx, rowY - Math.trunc(Menu.SET_SLIDER_H / 2), Settings.getMusicVolume(), this.setMusicBarH);
    }
    y += audioH + gap;

    // ===== VISUAL PANEL =====
    const visualH = 52 + 6 * Menu.SET_ROW_H + Menu.SET_PAD;
    if (y + visualH > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, Menu.SET_MARGIN, y, pw, visualH);
      g.setFont(PageRenderer.HEADING_FONT);
      g.setColor(PageRenderer.ACCENT);
      g.drawString('Visual', lx, y + 34);
      g.setColor(PageRenderer.BORDER);
      g.fillRect(lx, y + 46, pw - Menu.SET_PAD * 2, 1);

      // Screen shake
      let rowY = y + 52 + Math.trunc(Menu.SET_ROW_H / 2);
      g.setFont(PageRenderer.BODY_FONT);
      g.setColor(PageRenderer.TEXT_SEC);
      g.drawString('Screen Shake', lx, rowY + 5);
      const shakeLabels = ['Off', 'Low', 'High'];
      this.renderOptionGroup(g, cx, rowY - Math.trunc(Menu.SET_OPT_H / 2), shakeLabels, Settings.getScreenShake(), 2);

      // Particle density
      rowY = y + 52 + Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2);
      g.setColor(PageRenderer.TEXT_SEC);
      g.drawString('Particle Density', lx, rowY + 5);
      const partLabels = ['Low', 'Medium', 'High'];
      this.renderOptionGroup(g, cx, rowY - Math.trunc(Menu.SET_OPT_H / 2), partLabels, Settings.getParticleDensity(), 5);

      // Show FPS
      rowY = y + 52 + 2 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2);
      g.setColor(PageRenderer.TEXT_SEC);
      g.drawString('Show FPS', lx, rowY + 5);
      this.renderToggle(g, cx, rowY - Math.trunc(Menu.SET_TOGGLE_H / 2), Settings.getShowFps(), this.setH[8]);

      // Player trail
      rowY = y + 52 + 3 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2);
      g.setColor(PageRenderer.TEXT_SEC);
      g.drawString('Player Trail', lx, rowY + 5);
      this.renderToggle(g, cx, rowY - Math.trunc(Menu.SET_TOGGLE_H / 2), Settings.getPlayerTrail(), this.setH[9]);

      // Grid dots
      rowY = y + 52 + 4 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2);
      g.setColor(PageRenderer.TEXT_SEC);
      g.drawString('Grid Dots', lx, rowY + 5);
      this.renderToggle(g, cx, rowY - Math.trunc(Menu.SET_TOGGLE_H / 2), Settings.getGridDots(), this.setH[10]);

      // Colorblind mode
      rowY = y + 52 + 5 * Menu.SET_ROW_H + Math.trunc(Menu.SET_ROW_H / 2);
      g.setColor(PageRenderer.TEXT_SEC);
      g.drawString('Colorblind Mode', lx, rowY + 5);
      this.renderToggle(g, cx, rowY - Math.trunc(Menu.SET_TOGGLE_H / 2), Settings.getColorblindMode(), this.setH[11]);
    }
    y += visualH + gap;

    // ===== CONTROLS PANEL =====
    const controlLineH = 28;
    const controlsH = 52 + 7 * controlLineH + Menu.SET_PAD;
    if (y + controlsH > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, Menu.SET_MARGIN, y, pw, controlsH);
      g.setFont(PageRenderer.HEADING_FONT);
      g.setColor(PageRenderer.ACCENT);
      g.drawString('Controls', lx, y + 34);
      g.setColor(PageRenderer.BORDER);
      g.fillRect(lx, y + 46, pw - Menu.SET_PAD * 2, 1);

      let ky = y + 68;
      g.setFont(PageRenderer.BODY_FONT);
      const controls: string[][] = [
        ['W / ↑  A / ←  S / ↓  D / →', 'Move'],
        ['Shift', 'Dash'],
        ['E', 'Slow Motion'],
        ['Space', 'Open Shop'],
        ['P / Esc', 'Pause'],
        ['Esc', 'Back (menus)'],
        ['Mouse Wheel', 'Scroll'],
      ];
      for (const c of controls) {
        g.setColor(PageRenderer.TEXT);
        g.drawString(c[0], lx, ky);
        g.setColor(PageRenderer.TEXT_MUTED);
        g.drawString(c[1], cx, ky);
        ky += controlLineH;
      }
    }
    y += controlsH + gap;

    // ===== DANGER ZONE PANEL =====
    const dangerH = 52 + Menu.SET_ROW_H + Menu.SET_PAD;
    if (y + dangerH > contentTop - 20 && y < contentBottom + 20) {
      // Danger panel with red border
      g.setColor(PageRenderer.SURFACE);
      g.fillRoundRect(Menu.SET_MARGIN, y, pw, dangerH, PageRenderer.R, PageRenderer.R);
      g.setColor(new Color(235, 87, 87, 60));
      g.drawRoundRect(Menu.SET_MARGIN, y, pw, dangerH, PageRenderer.R, PageRenderer.R);
      g.setFont(PageRenderer.HEADING_FONT);
      g.setColor(PageRenderer.DANGER);
      g.drawString('Danger Zone', lx, y + 34);
      g.setColor(new Color(235, 87, 87, 60));
      g.fillRect(lx, y + 46, pw - Menu.SET_PAD * 2, 1);

      const rowY = y + 52 + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_DANGER_H / 2);
      const dangerCx = Math.trunc((Game.WIDTH - (Menu.SET_DANGER_W * 2 + 20)) / 2);
      PageRenderer.drawDangerButton(g, dangerCx, rowY, Menu.SET_DANGER_W, Menu.SET_DANGER_H, 'Reset High Scores', this.setH[18]);
      PageRenderer.drawDangerButton(g, dangerCx + Menu.SET_DANGER_W + 20, rowY, Menu.SET_DANGER_W, Menu.SET_DANGER_H, 'Reset All Progress', this.setH[19]);
    }
    y += dangerH + gap;

    const totalHeight = y + Math.trunc(this.settingsScroll) - contentTop;
    const maxScroll = Math.max(0, totalHeight - (contentBottom - contentTop) + 20);
    this.settingsScrollTarget = Math.min(this.settingsScrollTarget, maxScroll);

    g.setClip(oldClip);

    // Scroll indicator
    if (maxScroll > 0) {
      const scrollPct = (this.settingsScrollTarget > 0) ? this.settingsScroll / maxScroll : 0;
      const trackH = contentBottom - contentTop - 20;
      const thumbH = Math.max(30, Math.trunc((contentBottom - contentTop) / totalHeight * trackH));
      const thumbY = contentTop + 10 + Math.trunc((trackH - thumbH) * scrollPct);
      g.setColor(new Color(40, 52, 70, 80));
      g.fillRoundRect(Game.WIDTH - 22, contentTop + 10, 6, trackH, 3, 3);
      g.setColor(new Color(78, 205, 196, 120));
      g.fillRoundRect(Game.WIDTH - 22, thumbY, 6, thumbH, 3, 3);
    }

    // Confirmation dialog overlay
    if (this.confirmAction !== null) {
      this.renderConfirmDialog(g);
    }
  }

  private renderSlider(g: Graphics2D, x: number, y: number, value: number, barH: number): void {
    // Background bar
    g.setColor(PageRenderer.lerp(PageRenderer.BORDER, new Color(55, 70, 90), barH));
    g.fillRoundRect(x, y, Menu.SET_SLIDER_W, Menu.SET_SLIDER_H, 3, 3);

    // Filled portion
    const filledW = Math.trunc(Menu.SET_SLIDER_W * value);
    if (filledW > 0) {
      g.setColor(PageRenderer.ACCENT);
      g.fillRoundRect(x, y, filledW, Menu.SET_SLIDER_H, 3, 3);
    }

    // Knob
    const knobSize = Menu.SET_SLIDER_KNOB + Math.trunc(3 * barH);
    g.setColor(PageRenderer.lerp(PageRenderer.TEXT, PageRenderer.ACCENT, barH));
    g.fillOval(x + filledW - Math.trunc(knobSize / 2), y + Math.trunc(Menu.SET_SLIDER_H / 2) - Math.trunc(knobSize / 2), knobSize, knobSize);

    // Percentage label
    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    const pct = Math.round(value * 100) + '%';
    g.drawString(pct, x + Menu.SET_SLIDER_W + 14, y + Math.trunc(Menu.SET_SLIDER_H / 2) + 5);
  }

  private renderOptionGroup(g: Graphics2D, x: number, y: number, labels: string[], selected: number, hoverOffset: number): void {
    for (let i = 0; i < labels.length; i++) {
      const ox = x + i * (Menu.SET_OPT_W + Menu.SET_OPT_GAP);
      const active = (i === selected);
      const h = this.setH[hoverOffset + i];
      if (active) {
        g.setColor(PageRenderer.ACCENT);
        g.fillRoundRect(ox, y, Menu.SET_OPT_W, Menu.SET_OPT_H, 6, 6);
        g.setFont(PageRenderer.LABEL_FONT);
        g.setColor(PageRenderer.BG_DARK);
      } else {
        g.setColor(PageRenderer.lerp(PageRenderer.SURFACE, new Color(38, 50, 68), h));
        g.fillRoundRect(ox, y, Menu.SET_OPT_W, Menu.SET_OPT_H, 6, 6);
        g.setColor(PageRenderer.lerp(PageRenderer.BORDER, PageRenderer.ACCENT, h * 0.4));
        g.drawRoundRect(ox, y, Menu.SET_OPT_W, Menu.SET_OPT_H, 6, 6);
        g.setFont(PageRenderer.LABEL_FONT);
        g.setColor(PageRenderer.lerp(PageRenderer.TEXT_SEC, PageRenderer.TEXT, h));
      }
      PageRenderer.drawCenteredString(g, labels[i], ox, y, Menu.SET_OPT_W, Menu.SET_OPT_H);
    }
  }

  private renderToggle(g: Graphics2D, x: number, y: number, on: boolean, hover: number): void {
    const bg: Color = on
      ? PageRenderer.lerp(PageRenderer.ACCENT, new Color(110, 225, 218), hover)
      : PageRenderer.lerp(PageRenderer.SURFACE_LIGHT, new Color(48, 62, 82), hover);
    const border: Color = on
      ? PageRenderer.lerp(PageRenderer.ACCENT, new Color(110, 225, 218), hover)
      : PageRenderer.lerp(PageRenderer.BORDER, PageRenderer.ACCENT, hover * 0.3);

    g.setColor(bg);
    g.fillRoundRect(x, y, Menu.SET_TOGGLE_W, Menu.SET_TOGGLE_H, Menu.SET_TOGGLE_H, Menu.SET_TOGGLE_H);
    if (!on) {
      g.setColor(border);
      g.drawRoundRect(x, y, Menu.SET_TOGGLE_W, Menu.SET_TOGGLE_H, Menu.SET_TOGGLE_H, Menu.SET_TOGGLE_H);
    }

    // Knob
    const knobSize = Menu.SET_TOGGLE_H - 6;
    const knobX = on ? x + Menu.SET_TOGGLE_W - knobSize - 3 : x + 3;
    g.setColor(on ? PageRenderer.BG_DARK : PageRenderer.lerp(PageRenderer.TEXT_SEC, PageRenderer.TEXT, hover));
    g.fillOval(knobX, y + 3, knobSize, knobSize);
  }

  private renderConfirmDialog(g: Graphics2D): void {
    // Dim overlay
    g.setColor(new Color(0, 0, 0, 160));
    g.fillRect(0, 0, Game.WIDTH, Game.HEIGHT);

    const dw = 400;
    const dh = 160;
    const dx = Math.trunc((Game.WIDTH - dw) / 2);
    const dy = Math.trunc((Game.HEIGHT - dh) / 2);

    // Dialog panel
    g.setColor(PageRenderer.SURFACE);
    g.fillRoundRect(dx, dy, dw, dh, PageRenderer.R, PageRenderer.R);
    g.setColor(PageRenderer.DANGER);
    g.drawRoundRect(dx, dy, dw, dh, PageRenderer.R, PageRenderer.R);

    // Title
    g.setFont(PageRenderer.HEADING_FONT);
    g.setColor(PageRenderer.DANGER);
    const title = this.confirmAction === 'scores' ? 'Reset High Scores?' : 'Reset All Progress?';
    let fm: FontMetrics = g.getFontMetrics();
    g.drawString(title, Math.trunc((Game.WIDTH - fm.stringWidth(title)) / 2), dy + 40);

    // Description
    g.setFont(PageRenderer.BODY_FONT);
    g.setColor(PageRenderer.TEXT_SEC);
    const desc = this.confirmAction === 'scores'
      ? 'All high scores will be permanently erased.'
      : 'All scores, attempts, and settings will be reset.';
    fm = g.getFontMetrics();
    g.drawString(desc, Math.trunc((Game.WIDTH - fm.stringWidth(desc)) / 2), dy + 72);

    // Buttons
    const btnY = dy + Math.trunc(dh / 2) + 30;
    const cxd = Math.trunc(Game.WIDTH / 2);
    PageRenderer.drawDangerButton(g, cxd - 130, btnY, 120, 38, 'Confirm', this.confirmYesH);
    PageRenderer.drawSecondaryButton(g, cxd + 10, btnY, 120, 38, 'Cancel', this.confirmNoH);
  }

  // ---------- Statistics ----------

  private renderStatistics(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Statistics');
    PageRenderer.drawBackButton(g, this.backH);

    const contentTop = 130;
    const contentBottom = Game.HEIGHT - 30;
    const margin = 50;
    const pw = Game.WIDTH - margin * 2;
    const gap = 12;
    const pad = 20;
    const rowH = 24;

    const oldClip: Rectangle | null = g.getClip();
    g.clipRect(0, contentTop, Game.WIDTH, contentBottom - contentTop);

    let y = contentTop - Math.trunc(this.settingsScroll);
    const lx = margin + pad;
    const vx = margin + pw - pad;

    // ===== OVERVIEW PANEL =====
    const overviewRows = 6;
    const overviewH = 52 + overviewRows * rowH + pad;
    if (y + overviewH > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, margin, y, pw, overviewH);
      g.setFont(PageRenderer.HEADING_FONT);
      g.setColor(PageRenderer.ACCENT);
      g.drawString('Overview', lx, y + 34);
      g.setColor(PageRenderer.BORDER);
      g.fillRect(lx, y + 46, pw - pad * 2, 1);

      let ry = y + 64;
      this.statRow(g, lx, vx, ry, 'Player Level', String(Profile.getLevel())); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Total XP', String(Profile.getTotalXp())); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Total Games', String(Profile.getTotalGames())); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Total Deaths', String(Profile.getTotalDeaths())); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Total Time Played', Profile.getTotalTimePlayed()); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Lifetime Score', String(Profile.getTotalScore()));
    }
    y += overviewH + gap;

    // ===== PER-DIFFICULTY PANEL =====
    const diffNames = ['Normal', 'Hard', 'Insane'];
    const diffRows = 3 * 4 + 3; // 3 diffs * 4 rows + 3 headers
    const diffH = 52 + diffRows * rowH + pad;
    if (y + diffH > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, margin, y, pw, diffH);
      g.setFont(PageRenderer.HEADING_FONT);
      g.setColor(PageRenderer.ACCENT);
      g.drawString('Per Difficulty', lx, y + 34);
      g.setColor(PageRenderer.BORDER);
      g.fillRect(lx, y + 46, pw - pad * 2, 1);

      let ry = y + 64;
      for (let d = 0; d < 3; d++) {
        g.setFont(PageRenderer.LABEL_FONT);
        g.setColor(d === 0 ? PageRenderer.ACCENT : d === 1 ? PageRenderer.WARNING : PageRenderer.DANGER);
        g.drawString(diffNames[d].toUpperCase(), lx, ry);
        ry += rowH;
        this.statRow(g, lx + 12, vx, ry, 'Attempts', String(Profile.getAttempts(d))); ry += rowH;
        this.statRow(g, lx + 12, vx, ry, 'Best Score', String(Profile.getHighScore(d))); ry += rowH;
        this.statRow(g, lx + 12, vx, ry, 'Best Level', String(Profile.getHighLevel(d))); ry += rowH;
        this.statRow(g, lx + 12, vx, ry, 'Best Time', Profile.getBestTime(d)); ry += rowH;
      }
    }
    y += diffH + gap;

    // ===== COMBAT PANEL =====
    const combatRows = 8;
    const combatH = 52 + combatRows * rowH + pad;
    if (y + combatH > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, margin, y, pw, combatH);
      g.setFont(PageRenderer.HEADING_FONT);
      g.setColor(PageRenderer.ACCENT);
      g.drawString('Combat', lx, y + 34);
      g.setColor(PageRenderer.BORDER);
      g.fillRect(lx, y + 46, pw - pad * 2, 1);

      let ry = y + 64;
      this.statRow(g, lx, vx, ry, 'Total Damage Taken', String(Profile.getTotalDamageTaken())); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Longest No-Damage Streak', Profile.getLongestStreak() + 's'); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Avg Survival Time', Profile.getAvgSurvivalSeconds() + 's'); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Bosses Defeated', String(Profile.getTotalBossesDefeated())); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Favorite Difficulty', Profile.getDifficultyName(Profile.getFavoriteDifficulty())); ry += rowH;

      g.setFont(PageRenderer.LABEL_FONT);
      g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('ENEMIES ENCOUNTERED', lx, ry);
      ry += rowH;
      this.statRow(g, lx + 12, vx, ry, 'Basic / Fast / Smart / Hard',
        Profile.getEnemyBasic() + ' / ' + Profile.getEnemyFast()
        + ' / ' + Profile.getEnemySmart() + ' / ' + Profile.getEnemyHard()); ry += rowH;
      this.statRow(g, lx + 12, vx, ry, 'Bosses', String(Profile.getEnemyBoss()));
    }
    y += combatH + gap;

    // ===== UPGRADES PANEL =====
    const upgradeRows = 4;
    const upgradeH = 52 + upgradeRows * rowH + pad;
    if (y + upgradeH > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, margin, y, pw, upgradeH);
      g.setFont(PageRenderer.HEADING_FONT);
      g.setColor(PageRenderer.ACCENT);
      g.drawString('Upgrades', lx, y + 34);
      g.setColor(PageRenderer.BORDER);
      g.fillRect(lx, y + 46, pw - pad * 2, 1);

      let ry = y + 64;
      this.statRow(g, lx, vx, ry, 'Total Purchased', String(Profile.getTotalUpgrades())); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Health Upgrades', String(Profile.getTotalHealthUps())); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Speed Upgrades', String(Profile.getTotalSpeedUps())); ry += rowH;
      this.statRow(g, lx, vx, ry, 'Health Refills', String(Profile.getTotalRefills()));
    }
    y += upgradeH + gap;

    // ===== RECENT RUNS PANEL =====
    const histCount = Profile.getHistoryCount();
    const recentRows = Math.max(histCount, 1) + 1; // +1 for header row
    const recentH = 52 + recentRows * rowH + pad;
    if (y + recentH > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, margin, y, pw, recentH);
      g.setFont(PageRenderer.HEADING_FONT);
      g.setColor(PageRenderer.ACCENT);
      g.drawString('Recent Runs', lx, y + 34);
      g.setColor(PageRenderer.BORDER);
      g.fillRect(lx, y + 46, pw - pad * 2, 1);

      let ry = y + 64;

      if (histCount === 0) {
        g.setFont(PageRenderer.BODY_FONT);
        g.setColor(PageRenderer.TEXT_MUTED);
        g.drawString('No runs yet', lx, ry);
      } else {
        // Header
        g.setFont(PageRenderer.LABEL_FONT);
        g.setColor(PageRenderer.TEXT_MUTED);
        g.drawString('#', lx, ry);
        g.drawString('SCORE', lx + 40, ry);
        g.drawString('LEVEL', lx + 160, ry);
        g.drawString('DIFFICULTY', lx + 240, ry);
        ry += rowH;

        // Score chart — bar graph
        let maxScore = 1;
        for (let i = 0; i < histCount; i++) {
          const s = Profile.getRecentScore(i);
          if (s > maxScore) maxScore = s;
        }

        const chartX = lx + 340;
        const chartW = pw - pad * 2 - 340;

        for (let i = 0; i < histCount; i++) {
          const score = Profile.getRecentScore(i);
          const lvl = Profile.getRecentLevel(i);
          const diff = Profile.getRecentDifficulty(i);

          g.setFont(PageRenderer.SMALL_FONT);
          g.setColor(PageRenderer.TEXT_MUTED);
          g.drawString(String(i + 1), lx, ry);

          g.setColor(PageRenderer.TEXT);
          g.drawString(String(score), lx + 40, ry);
          g.drawString(String(lvl), lx + 160, ry);

          const diffCol: Color = diff === 0 ? PageRenderer.ACCENT : diff === 1 ? PageRenderer.WARNING : PageRenderer.DANGER;
          g.setColor(diffCol);
          g.drawString(Profile.getDifficultyName(diff), lx + 240, ry);

          // Mini bar
          if (chartW > 20) {
            const barW = Math.trunc(score / maxScore * chartW);
            g.setColor(new Color(diffCol.getRed(), diffCol.getGreen(), diffCol.getBlue(), 60));
            g.fillRoundRect(chartX, ry - 10, barW, 12, 3, 3);
          }

          ry += rowH;
        }
      }
    }
    y += recentH + gap;

    const totalHeight = y + Math.trunc(this.settingsScroll) - contentTop;
    const maxScroll = Math.max(0, totalHeight - (contentBottom - contentTop) + 20);
    this.settingsScrollTarget = Math.min(this.settingsScrollTarget, maxScroll);

    g.setClip(oldClip);

    // Scroll indicator
    if (maxScroll > 0) {
      const scrollPct = (this.settingsScrollTarget > 0) ? this.settingsScroll / maxScroll : 0;
      const trackH = contentBottom - contentTop - 20;
      const thumbH = Math.max(30, Math.trunc((contentBottom - contentTop) / totalHeight * trackH));
      const thumbY = contentTop + 10 + Math.trunc((trackH - thumbH) * scrollPct);
      g.setColor(new Color(40, 52, 70, 80));
      g.fillRoundRect(Game.WIDTH - 22, contentTop + 10, 6, trackH, 3, 3);
      g.setColor(new Color(78, 205, 196, 120));
      g.fillRoundRect(Game.WIDTH - 22, thumbY, 6, thumbH, 3, 3);
    }
  }

  private statRow(g: Graphics2D, lx: number, vx: number, y: number, label: string, value: string): void {
    g.setFont(PageRenderer.BODY_FONT);
    g.setColor(PageRenderer.TEXT_SEC);
    g.drawString(label, lx, y);
    g.setColor(PageRenderer.TEXT);
    const fm: FontMetrics = g.getFontMetrics();
    g.drawString(value, vx - fm.stringWidth(value), y);
  }

  // ---------- Daily Challenge ----------

  private dailyPlayH = 0;

  private renderDailyPage(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Daily Challenge');
    PageRenderer.drawBackButton(g, this.backH);

    const cx = Math.trunc(Game.WIDTH / 2);

    // Today's info
    g.setFont(PageRenderer.SUBTITLE_FONT);
    g.setColor(PageRenderer.TEXT_SEC);
    const dayInfo = DailyChallenge.todayLabel() + '  —  ' + DailyChallenge.todayDifficultyName();
    let fm: FontMetrics = g.getFontMetrics();
    g.drawString(dayInfo, cx - Math.trunc(fm.stringWidth(dayInfo) / 2), 120);

    // Streak + stats row
    const statsY = 145;
    g.setFont(PageRenderer.BODY_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    const statsLine = 'Streak: ' + DailyChallenge.getCurrentStreak()
      + '   Best streak: ' + DailyChallenge.getBestStreak()
      + '   Completed: ' + DailyChallenge.getTotalCompleted()
      + '   Best score: ' + DailyChallenge.getBestDailyScore();
    fm = g.getFontMetrics();
    g.drawString(statsLine, cx - Math.trunc(fm.stringWidth(statsLine) / 2), statsY);

    // 28-day calendar grid (4 rows of 7)
    const cellW = 110;
    const cellH = 72;
    const cellGap = 8;
    const gridW = 7 * cellW + 6 * cellGap;
    const gridX = Math.trunc((Game.WIDTH - gridW) / 2);
    let gridY = 170;

    // Day-of-week headers
    const headers = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    g.setFont(PageRenderer.LABEL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    for (let d = 0; d < 7; d++) {
      const hx = gridX + d * (cellW + cellGap) + Math.trunc(cellW / 2);
      fm = g.getFontMetrics();
      g.drawString(headers[d], hx - Math.trunc(fm.stringWidth(headers[d]) / 2), gridY);
    }
    gridY += 16;

    const today = DailyChallenge.dayInCycle();

    for (let i = 0; i < 28; i++) {
      const col = i % 7;
      const row = Math.trunc(i / 7);
      const cellX = gridX + col * (cellW + cellGap);
      const cellY = gridY + row * (cellH + cellGap);

      const isToday = (i === today);
      const completed = DailyChallenge.isCompleted(i);
      const score = DailyChallenge.getScore(i);
      const diff = DailyChallenge.getDifficultyForDay(i);
      const diffCol: Color = diff === 0 ? PageRenderer.ACCENT : diff === 1 ? PageRenderer.WARNING : PageRenderer.DANGER;

      // Cell background
      const bg: Color = isToday ? new Color(25, 40, 55) : PageRenderer.SURFACE;
      g.setColor(bg);
      g.fillRoundRect(cellX, cellY, cellW, cellH, 6, 6);

      // Border — highlight today
      if (isToday) {
        g.setColor(new Color(120, 200, 255));
        g.drawRoundRect(cellX, cellY, cellW, cellH, 6, 6);
      } else {
        g.setColor(completed ? new Color(72, 199, 142, 60) : PageRenderer.BORDER);
        g.drawRoundRect(cellX, cellY, cellW, cellH, 6, 6);
      }

      // Week label on first column
      if (col === 0) {
        g.setFont(PageRenderer.SMALL_FONT);
        g.setColor(PageRenderer.TEXT_MUTED);
        g.drawString('W' + (row + 1), cellX + 4, cellY + 14);
      }

      // Difficulty dot
      g.setColor(diffCol);
      g.fillOval(cellX + cellW - 14, cellY + 4, 8, 8);

      // Score or status
      if (completed) {
        g.setFont(PageRenderer.BUTTON_FONT);
        g.setColor(PageRenderer.TEXT);
        const scoreStr = String(score);
        fm = g.getFontMetrics();
        g.drawString(scoreStr, cellX + Math.trunc((cellW - fm.stringWidth(scoreStr)) / 2), cellY + 38);

        // Checkmark
        g.setFont(PageRenderer.SMALL_FONT);
        g.setColor(new Color(72, 199, 142));
        g.drawString('✓', cellX + Math.trunc(cellW / 2) - 4, cellY + 56);
      } else if (isToday) {
        g.setFont(PageRenderer.SMALL_FONT);
        const canPlayCell = DailyChallenge.canPlay();
        g.setColor(canPlayCell ? new Color(120, 200, 255) : PageRenderer.TEXT_MUTED);
        const status = canPlayCell ? 'PLAY' : 'DONE';
        fm = g.getFontMetrics();
        g.drawString(status, cellX + Math.trunc((cellW - fm.stringWidth(status)) / 2), cellY + 42);
      } else {
        g.setFont(PageRenderer.SMALL_FONT);
        g.setColor(new Color(40, 50, 65));
        const dash = '—';
        fm = g.getFontMetrics();
        g.drawString(dash, cellX + Math.trunc((cellW - fm.stringWidth(dash)) / 2), cellY + 42);
      }
    }

    // Play button
    const playX = Math.trunc((Game.WIDTH - 260) / 2);
    const playY = Game.HEIGHT - 90;
    const canPlay = DailyChallenge.canPlay();
    const playHovered = this.hit(this.mouseX, this.mouseY, playX, playY, 260, 50);
    this.dailyPlayH += ((playHovered ? 1 : 0) - this.dailyPlayH) * 0.14;

    if (canPlay) {
      PageRenderer.drawPrimaryButton(g, playX, playY, 260, 50,
        "Play Today's Challenge", this.dailyPlayH);
    } else {
      g.setColor(PageRenderer.SURFACE);
      g.fillRoundRect(playX, playY, 260, 50, PageRenderer.R, PageRenderer.R);
      g.setColor(PageRenderer.BORDER);
      g.drawRoundRect(playX, playY, 260, 50, PageRenderer.R, PageRenderer.R);
      g.setFont(PageRenderer.BUTTON_FONT);
      g.setColor(PageRenderer.TEXT_MUTED);
      PageRenderer.drawCenteredString(g, 'Completed Today', playX, playY, 260, 50);
    }
  }

  // ---------- Coin Shop ----------

  private static readonly SHOP_CARD_W = 280;
  private static readonly SHOP_CARD_H = 80;
  private static readonly SHOP_COLS = 3;
  private static readonly SHOP_GAP = 14;
  private static readonly COIN_GOLD = new Color(255, 210, 80);

  private handleCoinShopClick(mx: number, my: number): void {
    if (this.hitBack()) { Game.gameState = STATE.Menu; this.resetHover(); return; }

    const contentTop = 150;
    const contentBottom = Game.HEIGHT - 30;
    if (my < contentTop || my > contentBottom) return;

    const totalW = Menu.SHOP_CARD_W * Menu.SHOP_COLS + Menu.SHOP_GAP * (Menu.SHOP_COLS - 1);
    const gridX = Math.trunc((Game.WIDTH - totalW) / 2);
    let gridY = contentTop - Math.trunc(this.settingsScroll);

    let lastCat = -1;
    let row = 0, col = 0;
    for (let i = 0; i < CoinShop.ITEM_COUNT; i++) {
      const item: Item = CoinShop.getItem(i)!;
      if (item.category !== lastCat) {
        if (col > 0) { row++; col = 0; }
        gridY += (lastCat >= 0 ? 10 : 0) + 36;
        lastCat = item.category;
      }
      const cx = gridX + col * (Menu.SHOP_CARD_W + Menu.SHOP_GAP);
      const cy = gridY + row * (Menu.SHOP_CARD_H + Menu.SHOP_GAP);

      if (this.hit(mx, my, cx, cy, Menu.SHOP_CARD_W, Menu.SHOP_CARD_H) && !item.purchased && CoinShop.canAfford(i)) {
        CoinShop.purchase(i);
        return;
      }

      col++;
      if (col >= Menu.SHOP_COLS) { col = 0; row++; }
    }
  }

  private renderCoinShop(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Shop');
    PageRenderer.drawBackButton(g, this.backH);

    // Coin balance
    g.setFont(PageRenderer.SUBTITLE_FONT);
    g.setColor(Menu.COIN_GOLD);
    const bal = Profile.getCoins() + ' coins';
    const fm: FontMetrics = g.getFontMetrics();
    g.drawString(bal, Math.trunc((Game.WIDTH - fm.stringWidth(bal)) / 2), 125);

    const contentTop = 150;
    const contentBottom = Game.HEIGHT - 30;
    const totalW = Menu.SHOP_CARD_W * Menu.SHOP_COLS + Menu.SHOP_GAP * (Menu.SHOP_COLS - 1);
    const gridX = Math.trunc((Game.WIDTH - totalW) / 2);

    const oldClip: Rectangle | null = g.getClip();
    g.clipRect(0, contentTop, Game.WIDTH, contentBottom - contentTop);

    let gridY = contentTop - Math.trunc(this.settingsScroll);
    let lastCat = -1;
    let row = 0, col = 0;

    for (let i = 0; i < CoinShop.ITEM_COUNT; i++) {
      const item: Item = CoinShop.getItem(i)!;

      // Category header
      if (item.category !== lastCat) {
        if (col > 0) { row++; col = 0; }
        const headerY = gridY + row * (Menu.SHOP_CARD_H + Menu.SHOP_GAP) + (lastCat >= 0 ? 10 : 0);
        gridY += (lastCat >= 0 ? 10 : 0) + 36;
        lastCat = item.category;

        if (headerY > contentTop - 40 && headerY < contentBottom) {
          g.setFont(PageRenderer.HEADING_FONT);
          g.setColor(PageRenderer.ACCENT);
          g.drawString(CoinShop.CAT_NAMES[item.category], gridX, headerY + 24);
          g.setColor(PageRenderer.BORDER);
          g.fillRect(gridX, headerY + 30, totalW, 1);
        }
      }

      const cx = gridX + col * (Menu.SHOP_CARD_W + Menu.SHOP_GAP);
      const cy = gridY + row * (Menu.SHOP_CARD_H + Menu.SHOP_GAP);

      if (cy + Menu.SHOP_CARD_H > contentTop - 10 && cy < contentBottom + 10) {
        this.renderShopCard(g, cx, cy, item);
      }

      col++;
      if (col >= Menu.SHOP_COLS) { col = 0; row++; }
    }

    const totalHeight = gridY + (row + 1) * (Menu.SHOP_CARD_H + Menu.SHOP_GAP) + Math.trunc(this.settingsScroll) - contentTop;
    const maxScroll = Math.max(0, totalHeight - (contentBottom - contentTop) + 20);
    this.settingsScrollTarget = Math.min(this.settingsScrollTarget, maxScroll);

    g.setClip(oldClip);

    // Scroll indicator
    if (maxScroll > 0) {
      const scrollPct = (this.settingsScrollTarget > 0) ? this.settingsScroll / maxScroll : 0;
      const trackH = contentBottom - contentTop - 20;
      const thumbH = Math.max(30, Math.trunc((contentBottom - contentTop) / totalHeight * trackH));
      const thumbY = contentTop + 10 + Math.trunc((trackH - thumbH) * scrollPct);
      g.setColor(new Color(40, 52, 70, 80));
      g.fillRoundRect(Game.WIDTH - 22, contentTop + 10, 6, trackH, 3, 3);
      g.setColor(new Color(78, 205, 196, 120));
      g.fillRoundRect(Game.WIDTH - 22, thumbY, 6, thumbH, 3, 3);
    }
  }

  private renderShopCard(g: Graphics2D, x: number, y: number, item: Item): void {
    const purchased = item.purchased;
    const canAfford = !purchased && Number(Profile.getCoins()) >= item.cost;
    const hovered = this.hit(this.mouseX, this.mouseY, x, y, Menu.SHOP_CARD_W, Menu.SHOP_CARD_H);

    // Background
    const bg: Color = purchased ? new Color(22, 32, 28) :
      (hovered && canAfford) ? new Color(30, 40, 56) : PageRenderer.SURFACE;
    g.setColor(bg);
    g.fillRoundRect(x, y, Menu.SHOP_CARD_W, Menu.SHOP_CARD_H, 8, 8);

    // Border
    const border: Color = purchased ? new Color(72, 199, 142, 80) :
      (hovered && canAfford) ? Menu.COIN_GOLD : PageRenderer.BORDER;
    g.setColor(border);
    g.drawRoundRect(x, y, Menu.SHOP_CARD_W, Menu.SHOP_CARD_H, 8, 8);

    // Name
    g.setFont(PageRenderer.BUTTON_FONT);
    g.setColor(purchased ? PageRenderer.TEXT_MUTED : PageRenderer.TEXT);
    g.drawString(item.name, x + 12, y + 24);

    // Description
    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(purchased ? new Color(60, 70, 80) : PageRenderer.TEXT_SEC);
    g.drawString(item.description, x + 12, y + 44);

    if (purchased) {
      g.setFont(PageRenderer.LABEL_FONT);
      g.setColor(new Color(72, 199, 142));
      g.drawString('OWNED', x + Menu.SHOP_CARD_W - 60, y + 24);
    } else {
      // Price
      g.setFont(PageRenderer.LABEL_FONT);
      g.setColor(canAfford ? Menu.COIN_GOLD : PageRenderer.TEXT_MUTED);
      const price = item.cost + ' coins';
      g.drawString(price, x + 12, y + Menu.SHOP_CARD_H - 12);

      // Buy button
      if (canAfford && hovered) {
        g.setColor(Menu.COIN_GOLD);
        g.fillRoundRect(x + Menu.SHOP_CARD_W - 55, y + Menu.SHOP_CARD_H - 28, 44, 20, 4, 4);
        g.setFont(PageRenderer.SMALL_FONT);
        g.setColor(PageRenderer.BG_DARK);
        g.drawString('Buy', x + Menu.SHOP_CARD_W - 45, y + Menu.SHOP_CARD_H - 13);
      }
    }
  }

  // ---------- Loadout ----------

  private static readonly PERK_CARD_W = 240;
  private static readonly PERK_CARD_H = 110;
  private static readonly PERK_GAP = 16;
  private static readonly PERK_COLS = 4;
  private static readonly START_BTN_W = 280;
  private static readonly START_BTN_H = 50;
  private loadoutStartH = 0;

  private static perkGridX(): number {
    return Math.trunc((Game.WIDTH - (Menu.PERK_CARD_W * Menu.PERK_COLS + Menu.PERK_GAP * (Menu.PERK_COLS - 1))) / 2);
  }

  private handleLoadoutClick(mx: number, my: number): void {
    // Back to select
    if (this.hitBack()) { Game.gameState = STATE.Select; this.resetHover(); return; }

    // Start button
    const startX = Math.trunc((Game.WIDTH - Menu.START_BTN_W) / 2);
    const startY = Game.HEIGHT - 90;
    if (this.hit(mx, my, startX, startY, Menu.START_BTN_W, Menu.START_BTN_H)) {
      this.launchPendingGame();
      return;
    }

    // Perk cards
    const gridX = Menu.perkGridX();
    const gridY = 200;
    for (let i = 0; i < Perks.PERK_COUNT; i++) {
      const col = i % Menu.PERK_COLS;
      const row = Math.trunc(i / Menu.PERK_COLS);
      const cx = gridX + col * (Menu.PERK_CARD_W + Menu.PERK_GAP);
      const cy = gridY + row * (Menu.PERK_CARD_H + Menu.PERK_GAP);
      if (this.hit(mx, my, cx, cy, Menu.PERK_CARD_W, Menu.PERK_CARD_H)) {
        Perks.toggleEquip(i);
        return;
      }
    }
  }

  private launchPendingGame(): void {
    if (this.pendingDifficulty < 0) return;
    let firstEnemy: GameObject;
    if (this.pendingDifficulty === 0) {
      firstEnemy = new BasicEnemy(Game.WIDTH - 50, Game.HEIGHT - 50, ID.BasicEnemy, this.handler);
    } else if (this.pendingDifficulty === 3) {
      // Combat mode — first enemy is a basic with HP
      const be = new BasicEnemy(Game.WIDTH - 50, Game.HEIGHT - 50, ID.BasicEnemy, this.handler);
      be.setCombatHp(30);
      firstEnemy = be;
    } else {
      firstEnemy = new HardEnemy(Game.WIDTH - 100, Game.HEIGHT - 100, ID.BasicEnemy, this.handler);
    }
    this.startGame(this.pendingDifficulty, firstEnemy);
  }

  private renderLoadout(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Loadout');
    PageRenderer.drawBackButton(g, this.backH);

    // Difficulty label
    const diffName = this.pendingDifficulty === 0 ? 'Normal' : this.pendingDifficulty === 1 ? 'Hard' : 'Insane';
    const diffCol: Color = this.pendingDifficulty === 0 ? PageRenderer.ACCENT : this.pendingDifficulty === 1 ? PageRenderer.WARNING : PageRenderer.DANGER;
    g.setFont(PageRenderer.SUBTITLE_FONT);
    g.setColor(diffCol);
    let fm: FontMetrics = g.getFontMetrics();
    g.drawString(diffName, Math.trunc((Game.WIDTH - fm.stringWidth(diffName)) / 2), 122);

    g.setFont(PageRenderer.BODY_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    const hint = 'Select up to 2 perks  (' + Perks.getEquippedCount() + '/2)';
    fm = g.getFontMetrics();
    g.drawString(hint, Math.trunc((Game.WIDTH - fm.stringWidth(hint)) / 2), 155);

    // Perk grid
    const gridX = Menu.perkGridX();
    const gridY = 180;

    for (let i = 0; i < Perks.PERK_COUNT; i++) {
      const col = i % Menu.PERK_COLS;
      const row = Math.trunc(i / Menu.PERK_COLS);
      const cx = gridX + col * (Menu.PERK_CARD_W + Menu.PERK_GAP);
      const cy = gridY + row * (Menu.PERK_CARD_H + Menu.PERK_GAP);

      const unlocked = Perks.isUnlocked(i);
      const equipped = Perks.isEquipped(i);
      const hovered = this.hit(this.mouseX, this.mouseY, cx, cy, Menu.PERK_CARD_W, Menu.PERK_CARD_H);

      this.renderPerkCard(g, cx, cy, i, unlocked, equipped, hovered);
    }

    // Equipped summary
    const sumY = gridY + Math.trunc((Perks.PERK_COUNT + Menu.PERK_COLS - 1) / Menu.PERK_COLS) * (Menu.PERK_CARD_H + Menu.PERK_GAP) + 10;
    this.renderEquippedSummary(g, sumY);

    // Start button
    const startX = Math.trunc((Game.WIDTH - Menu.START_BTN_W) / 2);
    const startY = Game.HEIGHT - 90;
    const startHovered = this.hit(this.mouseX, this.mouseY, startX, startY, Menu.START_BTN_W, Menu.START_BTN_H);
    this.loadoutStartH += ((startHovered ? 1 : 0) - this.loadoutStartH) * 0.14;
    PageRenderer.drawPrimaryButton(g, startX, startY, Menu.START_BTN_W, Menu.START_BTN_H, 'Start Run', this.loadoutStartH);
  }

  private renderPerkCard(g: Graphics2D, x: number, y: number, perkId: number,
                         unlocked: boolean, equipped: boolean, hovered: boolean): void {
    // Card background
    let bg: Color;
    if (equipped) bg = new Color(25, 45, 55);
    else if (hovered && unlocked) bg = new Color(30, 40, 56);
    else bg = PageRenderer.SURFACE;
    g.setColor(bg);
    g.fillRoundRect(x, y, Menu.PERK_CARD_W, Menu.PERK_CARD_H, 10, 10);

    // Border
    let border: Color;
    if (equipped) border = PageRenderer.ACCENT;
    else if (hovered && unlocked) border = new Color(60, 80, 100);
    else border = PageRenderer.BORDER;
    g.setColor(border);
    g.drawRoundRect(x, y, Menu.PERK_CARD_W, Menu.PERK_CARD_H, 10, 10);

    // Accent top strip
    if (equipped) {
      g.setColor(PageRenderer.ACCENT);
      g.fillRoundRect(x, y, Menu.PERK_CARD_W, 3, 3, 3);
    }

    if (unlocked) {
      // Icon
      g.setFont(new Font('Arial', Font.PLAIN, 22));
      g.setColor(equipped ? PageRenderer.ACCENT : PageRenderer.TEXT_SEC);
      g.drawString(Perks.ICONS[perkId], x + 14, y + 30);

      // Name
      g.setFont(PageRenderer.BUTTON_FONT);
      g.setColor(equipped ? PageRenderer.TEXT : PageRenderer.TEXT_SEC);
      g.drawString(Perks.NAMES[perkId], x + 44, y + 28);

      // Description
      g.setFont(PageRenderer.SMALL_FONT);
      g.setColor(equipped ? PageRenderer.TEXT_SEC : PageRenderer.TEXT_MUTED);
      // Word-wrap description into card width
      this.drawCardDesc(g, Perks.DESCRIPTIONS[perkId], x + 14, y + 48, Menu.PERK_CARD_W - 28);

      // Equipped badge
      if (equipped) {
        g.setFont(PageRenderer.LABEL_FONT);
        g.setColor(PageRenderer.ACCENT);
        g.drawString('EQUIPPED', x + Menu.PERK_CARD_W - 72, y + Menu.PERK_CARD_H - 10);
      }
    } else {
      // Locked state
      g.setFont(new Font('Arial', Font.BOLD, 20));
      g.setColor(new Color(50, 58, 72));
      g.drawString('○', x + 14, y + 30);

      g.setFont(PageRenderer.BUTTON_FONT);
      g.setColor(new Color(60, 70, 85));
      g.drawString(Perks.NAMES[perkId], x + 44, y + 28);

      g.setFont(PageRenderer.SMALL_FONT);
      g.setColor(new Color(50, 60, 75));
      g.drawString(Perks.getUnlockDesc(perkId), x + 14, y + 50);
    }
  }

  private drawCardDesc(g: Graphics2D, text: string, x: number, y: number, maxW: number): void {
    const fm: FontMetrics = g.getFontMetrics();
    const words = text.split(' ');
    let line = '';
    let cy = y;
    for (const word of words) {
      const test = line.length === 0 ? word : line + ' ' + word;
      if (fm.stringWidth(test) > maxW && line.length > 0) {
        g.drawString(line, x, cy);
        cy += 16;
        line = word;
      } else {
        line = test;
      }
    }
    if (line.length > 0) g.drawString(line, x, cy);
  }

  private renderEquippedSummary(g: Graphics2D, y: number): void {
    const cx = Math.trunc(Game.WIDTH / 2);
    g.setFont(PageRenderer.LABEL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);

    const slot1 = Perks.getEquipped(0);
    const slot2 = Perks.getEquipped(1);

    if (slot1 < 0 && slot2 < 0) {
      const msg = 'No perks selected — click a perk to equip it';
      const fm: FontMetrics = g.getFontMetrics();
      g.drawString(msg, cx - Math.trunc(fm.stringWidth(msg) / 2), y);
    } else {
      let sb = 'Active: ';
      if (slot1 >= 0) sb += Perks.NAMES[slot1];
      if (slot1 >= 0 && slot2 >= 0) sb += '  +  ';
      if (slot2 >= 0) sb += Perks.NAMES[slot2];
      g.setColor(PageRenderer.ACCENT);
      const fm: FontMetrics = g.getFontMetrics();
      g.drawString(sb, cx - Math.trunc(fm.stringWidth(sb) / 2), y);
    }
  }

  // ---------- Customize ----------

  // Layout
  private static readonly SKIN_CELL = 70;
  private static readonly SKIN_GAP = 12;
  private static readonly SKIN_COLS = 6;

  private handleCustomizeClick(mx: number, my: number): void {
    if (this.hitBack()) { Game.gameState = STATE.Profile; this.resetHover(); return; }

    const margin = 60;
    const pad = 20;

    // Shape grid — y starts at 190
    const gridY = 190;
    const gridX = margin + pad;
    for (let i = 0; i < PlayerSkins.SHAPE_COUNT; i++) {
      const col = i % Menu.SKIN_COLS;
      const row = Math.trunc(i / Menu.SKIN_COLS);
      const cx = gridX + col * (Menu.SKIN_CELL + Menu.SKIN_GAP);
      const cy = gridY + row * (Menu.SKIN_CELL + Menu.SKIN_GAP);
      if (this.hit(mx, my, cx, cy, Menu.SKIN_CELL, Menu.SKIN_CELL)) {
        if (PlayerSkins.isShapeUnlocked(i)) {
          PlayerSkins.setSelectedShape(i);
        }
        return;
      }
    }

    // Color grid — y starts at 390
    const colorY = 390;
    for (let i = 0; i < PlayerSkins.COLOR_COUNT; i++) {
      const col = i % Menu.SKIN_COLS;
      const row = Math.trunc(i / Menu.SKIN_COLS);
      const cx = gridX + col * (Menu.SKIN_CELL + Menu.SKIN_GAP);
      const cy = colorY + row * (Menu.SKIN_CELL + Menu.SKIN_GAP);
      if (this.hit(mx, my, cx, cy, Menu.SKIN_CELL, Menu.SKIN_CELL)) {
        if (PlayerSkins.isColorUnlocked(i)) {
          PlayerSkins.setSelectedColor(i);
        }
        return;
      }
    }
  }

  private renderCustomize(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Customize');
    PageRenderer.drawBackButton(g, this.backH);

    const margin = 60;
    const pad = 20;

    // ===== SHAPE SELECTOR =====
    g.setFont(PageRenderer.HEADING_FONT);
    g.setColor(PageRenderer.ACCENT);
    g.drawString('Shape', margin + pad, 170);

    const gridX = margin + pad;
    const gridY = 190;

    for (let i = 0; i < PlayerSkins.SHAPE_COUNT; i++) {
      const col = i % Menu.SKIN_COLS;
      const row = Math.trunc(i / Menu.SKIN_COLS);
      const cx = gridX + col * (Menu.SKIN_CELL + Menu.SKIN_GAP);
      const cy = gridY + row * (Menu.SKIN_CELL + Menu.SKIN_GAP);

      const unlocked = PlayerSkins.isShapeUnlocked(i);
      const selected = (i === PlayerSkins.getSelectedShape());
      const hovered = this.hit(this.mouseX, this.mouseY, cx, cy, Menu.SKIN_CELL, Menu.SKIN_CELL);

      // Cell background
      let bg: Color = selected ? new Color(30, 50, 60) : new Color(22, 30, 44);
      if (hovered && unlocked) bg = new Color(35, 48, 64);
      g.setColor(bg);
      g.fillRoundRect(cx, cy, Menu.SKIN_CELL, Menu.SKIN_CELL, 8, 8);

      // Border
      const border: Color = selected ? PageRenderer.ACCENT : hovered ? new Color(60, 75, 95) : PageRenderer.BORDER;
      g.setColor(border);
      g.drawRoundRect(cx, cy, Menu.SKIN_CELL, Menu.SKIN_CELL, 8, 8);

      if (unlocked) {
        // Draw the shape preview
        const previewSize = 30;
        const px = cx + Math.trunc((Menu.SKIN_CELL - previewSize) / 2);
        const py = cy + 6;
        const previewCol: Color = selected ? PageRenderer.ACCENT : PageRenderer.TEXT_SEC;
        PlayerSkins.drawShape(g, i, px, py, previewSize, 6, previewCol);

        // Name
        g.setFont(PageRenderer.SMALL_FONT);
        g.setColor(selected ? PageRenderer.ACCENT : PageRenderer.TEXT_MUTED);
        const fm: FontMetrics = g.getFontMetrics();
        const name = PlayerSkins.SHAPE_NAMES[i];
        g.drawString(name, cx + Math.trunc((Menu.SKIN_CELL - fm.stringWidth(name)) / 2), cy + Menu.SKIN_CELL - 8);
      } else {
        // Locked
        g.setFont(new Font('Arial', Font.BOLD, 20));
        g.setColor(new Color(50, 58, 72));
        let fm: FontMetrics = g.getFontMetrics();
        g.drawString('○', cx + Math.trunc((Menu.SKIN_CELL - fm.stringWidth('○')) / 2), cy + 34);

        g.setFont(PageRenderer.SMALL_FONT);
        g.setColor(new Color(55, 65, 80));
        fm = g.getFontMetrics();
        const lock = 'Locked';
        g.drawString(lock, cx + Math.trunc((Menu.SKIN_CELL - fm.stringWidth(lock)) / 2), cy + Menu.SKIN_CELL - 8);

        // Tooltip on hover
        if (hovered) {
          const desc = PlayerSkins.getShapeUnlockDesc(i);
          g.setFont(PageRenderer.SMALL_FONT);
          g.setColor(PageRenderer.TEXT_MUTED);
          fm = g.getFontMetrics();
          g.drawString(desc, cx, cy + Menu.SKIN_CELL + 14);
        }
      }
    }

    // ===== COLOR PALETTE SELECTOR =====
    g.setFont(PageRenderer.HEADING_FONT);
    g.setColor(PageRenderer.ACCENT);
    g.drawString('Color', margin + pad, 370);

    const colorY = 390;

    for (let i = 0; i < PlayerSkins.COLOR_COUNT; i++) {
      const col = i % Menu.SKIN_COLS;
      const row = Math.trunc(i / Menu.SKIN_COLS);
      const cx = gridX + col * (Menu.SKIN_CELL + Menu.SKIN_GAP);
      const cy = colorY + row * (Menu.SKIN_CELL + Menu.SKIN_GAP);

      const unlocked = PlayerSkins.isColorUnlocked(i);
      const selected = (i === PlayerSkins.getSelectedColor());
      const hovered = this.hit(this.mouseX, this.mouseY, cx, cy, Menu.SKIN_CELL, Menu.SKIN_CELL);

      // Cell background
      let bg: Color = selected ? new Color(30, 50, 60) : new Color(22, 30, 44);
      if (hovered && unlocked) bg = new Color(35, 48, 64);
      g.setColor(bg);
      g.fillRoundRect(cx, cy, Menu.SKIN_CELL, Menu.SKIN_CELL, 8, 8);

      // Border
      const border: Color = selected ? PageRenderer.ACCENT : hovered ? new Color(60, 75, 95) : PageRenderer.BORDER;
      g.setColor(border);
      g.drawRoundRect(cx, cy, Menu.SKIN_CELL, Menu.SKIN_CELL, 8, 8);

      if (unlocked) {
        // Color swatch
        const swatch: Color = PlayerSkins.COLOR_FILLS[i];
        const swatchSize = 24;
        const sx = cx + Math.trunc((Menu.SKIN_CELL - swatchSize) / 2);
        const sy = cy + 10;
        g.setColor(swatch);
        g.fillRoundRect(sx, sy, swatchSize, swatchSize, 6, 6);
        if (selected) {
          g.setColor(new Color(255, 255, 255, 60));
          g.drawRoundRect(sx, sy, swatchSize, swatchSize, 6, 6);
        }

        // Name
        g.setFont(PageRenderer.SMALL_FONT);
        g.setColor(selected ? swatch : PageRenderer.TEXT_MUTED);
        const fm: FontMetrics = g.getFontMetrics();
        const name = PlayerSkins.COLOR_NAMES[i];
        g.drawString(name, cx + Math.trunc((Menu.SKIN_CELL - fm.stringWidth(name)) / 2), cy + Menu.SKIN_CELL - 8);
      } else {
        // Locked
        g.setFont(new Font('Arial', Font.BOLD, 20));
        g.setColor(new Color(50, 58, 72));
        let fm: FontMetrics = g.getFontMetrics();
        g.drawString('○', cx + Math.trunc((Menu.SKIN_CELL - fm.stringWidth('○')) / 2), cy + 34);

        g.setFont(PageRenderer.SMALL_FONT);
        g.setColor(new Color(55, 65, 80));
        fm = g.getFontMetrics();
        const lock = 'Locked';
        g.drawString(lock, cx + Math.trunc((Menu.SKIN_CELL - fm.stringWidth(lock)) / 2), cy + Menu.SKIN_CELL - 8);

        if (hovered) {
          const desc = PlayerSkins.getColorUnlockDesc(i);
          g.setFont(PageRenderer.SMALL_FONT);
          g.setColor(PageRenderer.TEXT_MUTED);
          fm = g.getFontMetrics();
          g.drawString(desc, cx, cy + Menu.SKIN_CELL + 14);
        }
      }
    }

    // ===== LIVE PREVIEW =====
    const previewX = Game.WIDTH - margin - 200;
    const previewY = 170;
    PageRenderer.drawPanel(g, previewX, previewY, 180, 220);

    g.setFont(PageRenderer.LABEL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    g.drawString('PREVIEW', previewX + 60, previewY + 22);

    // Draw player preview with selected skin
    const pSize = 64;
    const ppx = previewX + Math.trunc((180 - pSize) / 2);
    const ppy = previewY + 50;

    // Glow
    const skinFill: Color = PlayerSkins.getSelectedFill();
    g.setColor(new Color(skinFill.getRed(), skinFill.getGreen(), skinFill.getBlue(), 30));
    PlayerSkins.drawShape(g, PlayerSkins.getSelectedShape(),
      ppx - 6, ppy - 6, pSize + 12, 14, g.getColor());

    // Main shape
    PlayerSkins.drawShape(g, PlayerSkins.getSelectedShape(), ppx, ppy, pSize, 10, skinFill);

    // Labels
    g.setFont(PageRenderer.BODY_FONT);
    g.setColor(PageRenderer.TEXT);
    let fm: FontMetrics = g.getFontMetrics();
    const shapeName = PlayerSkins.SHAPE_NAMES[PlayerSkins.getSelectedShape()];
    g.drawString(shapeName, previewX + Math.trunc((180 - fm.stringWidth(shapeName)) / 2), ppy + pSize + 28);

    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(skinFill);
    fm = g.getFontMetrics();
    const colorName = PlayerSkins.COLOR_NAMES[PlayerSkins.getSelectedColor()];
    g.drawString(colorName, previewX + Math.trunc((180 - fm.stringWidth(colorName)) / 2), ppy + pSize + 48);

    // Trail shape label
    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    const trailLabel = 'Trail: ' + this.getTrailName(PlayerSkins.getSelectedTrailShape());
    fm = g.getFontMetrics();
    g.drawString(trailLabel, previewX + Math.trunc((180 - fm.stringWidth(trailLabel)) / 2), ppy + pSize + 68);
  }

  private getTrailName(trailShape: number): string {
    switch (trailShape) {
      case Trail.SHAPE_CIRCLE: return 'Circle';
      case Trail.SHAPE_DIAMOND: return 'Diamond';
      case Trail.SHAPE_TRIANGLE: return 'Triangle';
      default: return 'Square';
    }
  }

  // ---------- Achievements ----------

  private static readonly ACH_GOLD = new Color(255, 210, 80);
  private static readonly ACH_LOCKED = new Color(40, 48, 62);

  private renderAchievements(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Achievements');
    PageRenderer.drawBackButton(g, this.backH);

    // Summary line
    g.setFont(PageRenderer.SUBTITLE_FONT);
    g.setColor(PageRenderer.TEXT_SEC);
    const summary = Achievements.getUnlockedCount() + ' / ' + Achievements.getCount() + ' unlocked';
    let fm: FontMetrics = g.getFontMetrics();
    g.drawString(summary, Math.trunc((Game.WIDTH - fm.stringWidth(summary)) / 2), 120);

    const contentTop = 140;
    const contentBottom = Game.HEIGHT - 30;
    const margin = 40;
    const pw = Game.WIDTH - margin * 2;
    const pad = 16;
    const gap = 10;
    const cardH = 54;
    const cardGap = 6;

    const oldClip: Rectangle | null = g.getClip();
    g.clipRect(0, contentTop, Game.WIDTH, contentBottom - contentTop);

    let y = contentTop - Math.trunc(this.settingsScroll);

    // Render by category
    for (let cat = 0; cat < 4; cat++) {
      const inCat: Achievement[] = [];
      for (const a of Achievements.getAll()) {
        if (a.category === cat) inCat.push(a);
      }
      if (inCat.length === 0) continue;

      const catUnlocked = Achievements.getUnlockedInCategory(cat);
      const catTotal = Achievements.getTotalInCategory(cat);

      // Category header
      const headerH = 40;
      if (y + headerH > contentTop - 20 && y < contentBottom + 20) {
        g.setFont(PageRenderer.HEADING_FONT);
        g.setColor(PageRenderer.ACCENT);
        g.drawString(Achievements.CAT_NAMES[cat], margin + pad, y + 28);

        g.setFont(PageRenderer.SMALL_FONT);
        g.setColor(PageRenderer.TEXT_MUTED);
        const catCount = catUnlocked + '/' + catTotal;
        fm = g.getFontMetrics();
        g.drawString(catCount, margin + pw - pad - fm.stringWidth(catCount), y + 28);

        g.setColor(PageRenderer.BORDER);
        g.fillRect(margin + pad, y + 34, pw - pad * 2, 1);
      }
      y += headerH;

      // Achievement cards
      for (const a of inCat) {
        if (y + cardH > contentTop - 10 && y < contentBottom + 10) {
          this.renderAchievementCard(g, margin, y, pw, cardH, a);
        }
        y += cardH + cardGap;
      }

      y += gap;
    }

    const totalHeight = y + Math.trunc(this.settingsScroll) - contentTop;
    const maxScroll = Math.max(0, totalHeight - (contentBottom - contentTop) + 20);
    this.settingsScrollTarget = Math.min(this.settingsScrollTarget, maxScroll);

    g.setClip(oldClip);

    // Scroll indicator
    if (maxScroll > 0) {
      const scrollPct = (this.settingsScrollTarget > 0) ? this.settingsScroll / maxScroll : 0;
      const trackH = contentBottom - contentTop - 20;
      const thumbH = Math.max(30, Math.trunc((contentBottom - contentTop) / totalHeight * trackH));
      const thumbY = contentTop + 10 + Math.trunc((trackH - thumbH) * scrollPct);
      g.setColor(new Color(40, 52, 70, 80));
      g.fillRoundRect(Game.WIDTH - 22, contentTop + 10, 6, trackH, 3, 3);
      g.setColor(new Color(78, 205, 196, 120));
      g.fillRoundRect(Game.WIDTH - 22, thumbY, 6, thumbH, 3, 3);
    }
  }

  private renderAchievementCard(g: Graphics2D, x: number, y: number, w: number, h: number,
                                a: Achievement): void {
    const unlocked = a.unlocked;

    // Card background
    const bg: Color = unlocked ? new Color(28, 36, 50) : new Color(18, 22, 32);
    g.setColor(bg);
    g.fillRoundRect(x, y, w, h, 8, 8);

    // Left accent strip
    const accent: Color = unlocked ? Menu.ACH_GOLD : Menu.ACH_LOCKED;
    g.setColor(accent);
    g.fillRoundRect(x, y, 4, h, 4, 4);

    // Trophy / lock icon
    g.setFont(new Font('Arial', Font.BOLD, 18));
    g.setColor(unlocked ? Menu.ACH_GOLD : new Color(50, 58, 72));
    g.drawString(unlocked ? '★' : '○', x + 16, y + Math.trunc(h / 2) + 7);

    // Name
    g.setFont(PageRenderer.BUTTON_FONT);
    g.setColor(unlocked ? PageRenderer.TEXT : new Color(80, 90, 105));
    g.drawString(a.name, x + 44, y + 22);

    // Description
    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(unlocked ? PageRenderer.TEXT_SEC : new Color(55, 65, 80));
    g.drawString(a.description, x + 44, y + 40);

    // Progress bar (if applicable and not unlocked)
    if (!unlocked) {
      const progress = Achievements.getProgress(a.id);
      if (progress >= 0 && progress < 1) {
        const barW = 100;
        const barH2 = 4;
        const barX = x + w - barW - 16;
        const barY = y + Math.trunc(h / 2) - Math.trunc(barH2 / 2);
        g.setColor(new Color(30, 38, 52));
        g.fillRoundRect(barX, barY, barW, barH2, 2, 2);
        const fillW = Math.trunc(barW * Math.min(progress, 1));
        if (fillW > 0) {
          g.setColor(new Color(78, 205, 196, 120));
          g.fillRoundRect(barX, barY, fillW, barH2, 2, 2);
        }
        g.setFont(PageRenderer.SMALL_FONT);
        g.setColor(PageRenderer.TEXT_MUTED);
        const pctStr = Math.trunc(progress * 100) + '%';
        g.drawString(pctStr, barX + barW + 6, barY + 4);
      }
    } else {
      // Unlocked checkmark
      g.setFont(PageRenderer.LABEL_FONT);
      g.setColor(Menu.ACH_GOLD);
      const fm: FontMetrics = g.getFontMetrics();
      const check = 'UNLOCKED';
      g.drawString(check, x + w - fm.stringWidth(check) - 16, y + Math.trunc(h / 2) + 5);
    }

    // Border
    g.setColor(unlocked ? new Color(255, 210, 80, 30) : new Color(40, 48, 62));
    g.drawRoundRect(x, y, w, h, 8, 8);
  }

  // ---------- About ----------

  private renderAbout(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'About');
    PageRenderer.drawBackButton(g, this.backH);

    const margin = 60;
    const panelW = Game.WIDTH - margin * 2;
    const pad = 20;
    const lineH = 22;
    const gap = 12;
    let y = 130;

    // The Game
    g.setFont(PageRenderer.BODY_FONT);
    const fm: FontMetrics = g.getFontMetrics();
    const gameDesc = 'Dotch. is a fast-paced dodge game where your only goal is survival. Navigate through waves of enemies that grow stronger with each level. Earn points to upgrade your speed, health, and survivability. Originally created in 2016 and rebuilt from the ground up in 2026.';
    const gameH = 52 + this.measureSectionHeight(gameDesc, panelW - pad * 2, fm, lineH) + pad;
    PageRenderer.drawPanel(g, margin, y, panelW, gameH);
    g.setFont(PageRenderer.HEADING_FONT);
    g.setColor(PageRenderer.ACCENT);
    g.drawString('The Game', margin + pad, y + 34);
    g.setColor(PageRenderer.BORDER);
    g.fillRect(margin + pad, y + 46, panelW - pad * 2, 1);
    g.setFont(PageRenderer.BODY_FONT);
    g.setColor(PageRenderer.TEXT_SEC);
    this.drawWrappedText(g, gameDesc, margin + pad, y + 68, panelW - pad * 2, lineH);
    y += gameH + gap;

    // Game Modes
    const modesDesc = 'Normal  —  Predictable bouncing enemies. Recommended for new players.\n\nHard  —  Enemies randomize direction on each bounce. Unpredictable and challenging.\n\nInsane  —  Fast, smart, and random enemies combined. For experienced players only.';
    const modesH = 52 + this.measureSectionHeight(modesDesc, panelW - pad * 2, fm, lineH) + pad;
    PageRenderer.drawPanel(g, margin, y, panelW, modesH);
    g.setFont(PageRenderer.HEADING_FONT);
    g.setColor(PageRenderer.ACCENT);
    g.drawString('Game Modes', margin + pad, y + 34);
    g.setColor(PageRenderer.BORDER);
    g.fillRect(margin + pad, y + 46, panelW - pad * 2, 1);
    g.setFont(PageRenderer.BODY_FONT);
    g.setColor(PageRenderer.TEXT_SEC);
    this.drawWrappedText(g, modesDesc, margin + pad, y + 68, panelW - pad * 2, lineH);
    y += modesH + gap;

    // Enemy Types
    const iconCol = margin + pad;
    const textCol = iconCol + 26;
    const enemyLineH = 28;
    const enemies: string[][] = [
      ['square', 'Basic', 'Bounces in straight lines at constant speed.', '235,87,87'],
      ['diamond', 'Fast', 'High speed diagonal movement across the arena.', '78,205,196'],
      ['circle', 'Smart', 'Tracks and follows the player continuously.', '199,125,255'],
      ['triangle', 'Hard', 'Changes direction randomly on every bounce.', '245,195,68'],
      ['boss', 'Boss', 'Large enemy that spawns bullets. Appears at milestone levels.', '235,87,87'],
    ];
    const enemyH = 52 + enemies.length * enemyLineH + pad;
    PageRenderer.drawPanel(g, margin, y, panelW, enemyH);
    g.setFont(PageRenderer.HEADING_FONT);
    g.setColor(PageRenderer.ACCENT);
    g.drawString('Enemy Types', margin + pad, y + 34);
    g.setColor(PageRenderer.BORDER);
    g.fillRect(margin + pad, y + 46, panelW - pad * 2, 1);

    let ey = y + 68;
    for (const e of enemies) {
      const rgb = e[3].split(',');
      const c = new Color(parseInt(rgb[0], 10), parseInt(rgb[1], 10), parseInt(rgb[2], 10));
      this.drawEnemyIcon(g, iconCol, ey - 12, 14, e[0], c);
      g.setFont(PageRenderer.BODY_FONT);
      g.setColor(PageRenderer.TEXT);
      g.drawString(e[1], textCol, ey);
      g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString(e[2], textCol + 80, ey);
      ey += enemyLineH;
    }
  }

  private drawEnemyIcon(g: Graphics2D, x: number, y: number, s: number, shape: string, c: Color): void {
    g.setColor(c);
    switch (shape) {
      case 'square': g.fillRoundRect(x, y, s, s, 4, 4); break;
      case 'diamond':
        g.fillPolygon([x + Math.trunc(s / 2), x + s, x + Math.trunc(s / 2), x],
          [y, y + Math.trunc(s / 2), y + s, y + Math.trunc(s / 2)], 4); break;
      case 'circle': g.fillOval(x, y, s, s); break;
      case 'triangle':
        g.fillPolygon([x + Math.trunc(s / 2), x + s, x], [y, y + s, y + s], 3); break;
      case 'boss':
        g.fillRoundRect(x, y, s, s, 4, 4);
        g.setColor(new Color(255, 120, 120));
        g.drawRoundRect(x, y, s, s, 4, 4); break;
    }
  }

  // ---------- Credits ----------

  private renderCredits(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Credits');
    PageRenderer.drawBackButton(g, this.backH);

    const margin = 60;
    const panelW = Game.WIDTH - margin * 2;
    const pad = 20;

    PageRenderer.drawPanel(g, margin, 130, panelW, 320);

    const x = margin + pad;
    let y = 170;
    const spacing = 52;

    this.drawCreditLine(g, x, y, 'Development', 'Maurice Boendermaker'); y += spacing;
    this.drawCreditLine(g, x, y, 'Original Visual Graphic Design', 'Jeffrey Persoon'); y += spacing;
    this.drawCreditLine(g, x, y, 'Built With', 'Java AWT / Swing'); y += spacing;
    this.drawCreditLine(g, x, y, 'Audio', 'Virtual Riot  /  MDK  /  Desmeon  /  Pegboard Nerds  /  Avicii  /  Skrillex'); y += spacing;
    this.drawCreditLine(g, x, y, 'Version', 'v4.1  —  March 2026'); y += spacing;

    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    const footer = 'First release: August 2016';
    g.drawString(footer, x, y + 10);
  }

  private drawCreditLine(g: Graphics2D, x: number, y: number, role: string, name: string): void {
    g.setFont(PageRenderer.LABEL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    g.drawString(role.toUpperCase(), x, y);
    g.setFont(PageRenderer.BODY_FONT);
    g.setColor(PageRenderer.TEXT);
    g.drawString(name, x, y + 20);
  }

  // ---------- Bottom Links Helper ----------

  private getBottomLinkXs(): number[] {
    const fm: FontMetrics = new FontMetrics(PageRenderer.SMALL_FONT);
    const a = 'About', d = '  |  ', cl = 'Changelog', d2 = '  |  ', cr = 'Credits';
    const total = fm.stringWidth(a + d + cl + d2 + cr);
    const sx = Math.trunc((Game.WIDTH - total) / 2);
    const x1 = sx;
    const x2 = x1 + fm.stringWidth(a + d);
    const x3 = x2 + fm.stringWidth(cl + d2);
    const x4 = x3 + fm.stringWidth(cr);
    return [x1, x2, x3, x4];
  }

  private drawBottomLinks(g: Graphics2D): void {
    g.setFont(PageRenderer.SMALL_FONT);
    const fm: FontMetrics = g.getFontMetrics();
    const a = 'About', d = '  |  ', cl = 'Changelog', d2 = '  |  ', cr = 'Credits';
    const total = fm.stringWidth(a + d + cl + d2 + cr);
    let x = Math.trunc((Game.WIDTH - total) / 2);

    g.setColor(PageRenderer.lerp(PageRenderer.TEXT_SEC, PageRenderer.ACCENT, this.aboutH));
    g.drawString(a, x, 680);
    x += fm.stringWidth(a);
    g.setColor(PageRenderer.TEXT_MUTED);
    g.drawString(d, x, 680);
    x += fm.stringWidth(d);
    g.setColor(PageRenderer.lerp(PageRenderer.TEXT_SEC, PageRenderer.ACCENT, this.changelogH));
    g.drawString(cl, x, 680);
    x += fm.stringWidth(cl);
    g.setColor(PageRenderer.TEXT_MUTED);
    g.drawString(d2, x, 680);
    x += fm.stringWidth(d2);
    g.setColor(PageRenderer.lerp(PageRenderer.TEXT_SEC, PageRenderer.ACCENT, this.creditsLinkH));
    g.drawString(cr, x, 680);
  }

  // ---------- Changelog ----------

  private renderUpdates(g: Graphics2D): void {
    PageRenderer.drawBackground(g);
    PageRenderer.drawTitle(g, 'Changelog');
    PageRenderer.drawBackButton(g, this.backH);

    const contentTop = 130;
    const contentBottom = Game.HEIGHT - 30;
    const margin = 60;
    const pw = Game.WIDTH - margin * 2;
    const lineH = 22;
    const gap = 14;

    const oldClip: Rectangle | null = g.getClip();
    g.clipRect(0, contentTop, Game.WIDTH, contentBottom - contentTop);

    let y = contentTop - Math.trunc(this.settingsScroll);

    // ===== v4.1 =====
    const v41lines = [
      '- New main menu layout: Play, Profile, Shop, Settings, Help.',
      '- New Profile page with Customize, Statistics, and Achievements.',
      '- Ability icons: shield shape, double chevrons for dash, hourglass for slow-mo.',
      '- Slow-motion charges now regenerate over time instead of being one-time use.',
      '- Chrome/white time warp visual effect when slow-motion is active.',
      '- Quit button replaced with icon. Music player moved to compact icon.',
      '- Daily streak and now playing info repositioned in menu.',
      '- Score milestone blink now triggers every 1000 points.',
      '- Dynamic spacing for wave/level/points HUD to prevent text overlap.',
      '- Improved score label padding and HP/SPD/REF bar alignment.',
      '- Button padding added to menu and profile page.',
      '- Performance: render loop capped at 60fps (was uncapped).',
      '- Performance: deferred object removal eliminates O(n) mid-tick scans.',
      '- Performance: cached Rectangles, Colors, and AlphaComposites in hot paths.',
      '- Temporarily disabled SFX volume slider and language options.',
    ];
    const v41h = 52 + v41lines.length * lineH + 16;
    if (y + v41h > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, margin, y, pw, v41h);
      g.setFont(PageRenderer.HEADING_FONT); g.setColor(PageRenderer.ACCENT);
      g.drawString('v4.1', 85, y + 32);
      g.setFont(PageRenderer.SMALL_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('26.03.2026', 140, y + 32);
      g.setColor(PageRenderer.BORDER); g.fillRect(85, y + 44, pw - 50, 1);
      g.setFont(PageRenderer.BODY_FONT); g.setColor(PageRenderer.TEXT_SEC);
      let ly = y + 66;
      for (const line of v41lines) { g.drawString(line, 85, ly); ly += lineH; }
    }
    y += v41h + gap;

    // ===== v4.0 =====
    const v4lines = [
      '- Player abilities: Dash (Shift), Shield (auto), Slow-Motion (E).',
      '- 4 boss types: Classic, Splitter, Laser, Swarm — rotating per level.',
      '- Endless scaling: formula-based spawning after scripted levels.',
      '- Boss every 10th level across all difficulties.',
      '- Player XP and profile level system with persistent progression.',
      '- 50 achievements across Survival, Skill, Persistence, and Challenge.',
      '- Player skin customization: 6 shapes, 8 color palettes.',
      '- Loadout system: 8 perks, choose up to 2 before each run.',
      '- Coin Shop: 25 permanent unlocks purchased with earned coins.',
      '- Statistics dashboard with lifetime stats and recent run history.',
      '- Daily Challenge: seeded run per day, 28-day cycle, streak tracking.',
      '- Settings page: volume, screen shake, particles, FPS, colorblind mode.',
      '- Settings accessible from pause menu during gameplay.',
      '- Redesigned main menu with unified button layout.',
    ];
    const v4h = 52 + v4lines.length * lineH + 16;
    if (y + v4h > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, margin, y, pw, v4h);
      g.setFont(PageRenderer.HEADING_FONT); g.setColor(PageRenderer.TEXT);
      g.drawString('v4.0', 85, y + 32);
      g.setFont(PageRenderer.SMALL_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('25.03.2026', 140, y + 32);
      g.setColor(PageRenderer.BORDER); g.fillRect(85, y + 44, pw - 50, 1);
      g.setFont(PageRenderer.BODY_FONT); g.setColor(PageRenderer.TEXT_SEC);
      let ly = y + 66;
      for (const line of v4lines) { g.drawString(line, 85, ly); ly += lineH; }
    }
    y += v4h + gap;

    // ===== v3.0 =====
    const v3lines = [
      '- Complete visual overhaul with modern dark theme.',
      '- Fullscreen mode with automatic resolution scaling.',
      '- Built-in music player with playback controls.',
    ];
    const v3h = 52 + v3lines.length * lineH + 16;
    if (y + v3h > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, margin, y, pw, v3h);
      g.setFont(PageRenderer.HEADING_FONT); g.setColor(PageRenderer.TEXT);
      g.drawString('v3.0', 85, y + 32);
      g.setFont(PageRenderer.SMALL_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('25.03.2026', 140, y + 32);
      g.setColor(PageRenderer.BORDER); g.fillRect(85, y + 44, pw - 50, 1);
      g.setFont(PageRenderer.BODY_FONT); g.setColor(PageRenderer.TEXT_SEC);
      let ly = y + 66;
      for (const line of v3lines) { g.drawString(line, 85, ly); ly += lineH; }
    }
    y += v3h + gap;

    // ===== v2.0 =====
    const v2lines = [
      '- Complete rewrite and overhaul of legacy codebase.',
      '- Fixed resolution bugs, removed duplicate classes, cleaned up all dead code.',
      '- No new features — focused on code quality and stability.',
    ];
    const v2h = 52 + v2lines.length * lineH + 16;
    if (y + v2h > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, margin, y, pw, v2h);
      g.setFont(PageRenderer.HEADING_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('v2.0', 85, y + 32);
      g.setFont(PageRenderer.SMALL_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('25.03.2026', 140, y + 32);
      g.setColor(PageRenderer.BORDER); g.fillRect(85, y + 44, pw - 50, 1);
      g.setFont(PageRenderer.BODY_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      let ly = y + 66;
      for (const line of v2lines) { g.drawString(line, 85, ly); ly += lineH; }
    }
    y += v2h + gap;

    // ===== v1.0 =====
    const v1lines = [
      '- Name changed from "Dodge Game!" to "Dotch".',
      '- Game layout updated. Added contact page and language support.',
    ];
    const v1h = 52 + v1lines.length * lineH + 16;
    if (y + v1h > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, margin, y, pw, v1h);
      g.setFont(PageRenderer.HEADING_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('v1.0', 85, y + 32);
      g.setFont(PageRenderer.SMALL_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('10.11.2019', 140, y + 32);
      g.setColor(PageRenderer.BORDER); g.fillRect(85, y + 44, pw - 50, 1);
      g.setFont(PageRenderer.BODY_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      let ly = y + 66;
      for (const line of v1lines) { g.drawString(line, 85, ly); ly += lineH; }
    }
    y += v1h + gap;

    // ===== v0.1 =====
    const v01lines = [
      '- Initial release with core game mechanics.',
      '- Player movement, enemy spawning, collision detection, and scoring.',
      '- Three difficulty modes, boss fights, HUD, and in-game shop.',
    ];
    const v01h = 52 + v01lines.length * lineH + 16;
    if (y + v01h > contentTop - 20 && y < contentBottom + 20) {
      PageRenderer.drawPanel(g, margin, y, pw, v01h);
      g.setFont(PageRenderer.HEADING_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('v0.1', 85, y + 32);
      g.setFont(PageRenderer.SMALL_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('09.08.2016', 138, y + 32);
      g.setColor(PageRenderer.BORDER); g.fillRect(85, y + 44, pw - 50, 1);
      g.setFont(PageRenderer.BODY_FONT); g.setColor(PageRenderer.TEXT_MUTED);
      let ly = y + 66;
      for (const line of v01lines) { g.drawString(line, 85, ly); ly += lineH; }
    }
    y += v01h + gap;

    const totalHeight = y + Math.trunc(this.settingsScroll) - contentTop;
    const maxScroll = Math.max(0, totalHeight - (contentBottom - contentTop) + 20);
    this.settingsScrollTarget = Math.min(this.settingsScrollTarget, maxScroll);

    g.setClip(oldClip);

    if (maxScroll > 0) {
      const scrollPct = (this.settingsScrollTarget > 0) ? this.settingsScroll / maxScroll : 0;
      const trackH = contentBottom - contentTop - 20;
      const thumbH = Math.max(30, Math.trunc((contentBottom - contentTop) / totalHeight * trackH));
      const thumbY = contentTop + 10 + Math.trunc((trackH - thumbH) * scrollPct);
      g.setColor(new Color(40, 52, 70, 80));
      g.fillRoundRect(Game.WIDTH - 22, contentTop + 10, 6, trackH, 3, 3);
      g.setColor(new Color(78, 205, 196, 120));
      g.fillRoundRect(Game.WIDTH - 22, thumbY, 6, thumbH, 3, 3);
    }
  }

  // ---------- End / Game Over ----------

  private renderEnd(g: Graphics2D): void {
    PageRenderer.drawBackground(g);

    const cx = Math.trunc(Game.WIDTH / 2);

    // Title
    g.setFont(PageRenderer.TITLE_FONT);
    g.setColor(PageRenderer.DANGER);
    const go = 'Game Over';
    let fm: FontMetrics = g.getFontMetrics();
    g.drawString(go, cx - Math.trunc(fm.stringWidth(go) / 2), 72);

    // Performance rating + attempt
    const rating = this.getRating(this.game.lastScore, this.game.lastLevel);
    g.setFont(PageRenderer.SUBTITLE_FONT);
    g.setColor(PageRenderer.ACCENT);
    fm = g.getFontMetrics();
    const ratingLine = rating + '  -  Attempt #' + Game.currentAttempt;
    g.drawString(ratingLine, cx - Math.trunc(fm.stringWidth(ratingLine) / 2), 105);

    // Score panel — centered
    const scoreW = 320;
    const scoreX = cx - Math.trunc(scoreW / 2);
    PageRenderer.drawPanel(g, scoreX, 125, scoreW, 110);
    g.setFont(PageRenderer.LABEL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    const sl = 'FINAL SCORE';
    fm = g.getFontMetrics();
    g.drawString(sl, cx - Math.trunc(fm.stringWidth(sl) / 2), 155);
    g.setFont(new Font('Arial', Font.BOLD, 52));
    g.setColor(this.game.lastIsHighScore ? new Color(255, 210, 80) : PageRenderer.TEXT);
    const scoreStr = String(this.game.lastScore);
    fm = g.getFontMetrics();
    g.drawString(scoreStr, cx - Math.trunc(fm.stringWidth(scoreStr) / 2), 218);

    if (this.game.lastIsHighScore) {
      g.setFont(PageRenderer.LABEL_FONT);
      g.setColor(new Color(255, 210, 80));
      const hs = 'NEW HIGH SCORE';
      fm = g.getFontMetrics();
      g.drawString(hs, cx - Math.trunc(fm.stringWidth(hs) / 2), 232);
    }

    // Stats panels — two columns
    const gap = 14;
    const panelW = Math.trunc((Game.WIDTH - 120 - gap) / 2);
    const leftX = 60;
    const rightX = leftX + panelW + gap;
    const statsY = 252;

    // Left — Run Stats
    PageRenderer.drawPanel(g, leftX, statsY, panelW, 196);
    g.setFont(PageRenderer.HEADING_FONT);
    g.setColor(PageRenderer.ACCENT);
    g.drawString('Run Stats', leftX + 20, statsY + 32);
    g.setColor(PageRenderer.BORDER);
    g.fillRect(leftX + 20, statsY + 44, panelW - 40, 1);

    let sy = statsY + 70;
    this.drawStatRow(g, leftX + 20, sy, panelW - 40, 'Difficulty', this.game.lastDifficulty); sy += 26;
    this.drawStatRow(g, leftX + 20, sy, panelW - 40, 'Level reached', String(this.game.lastLevel)); sy += 26;
    this.drawStatRow(g, leftX + 20, sy, panelW - 40, 'Time survived', this.game.lastTime); sy += 26;
    this.drawStatRow(g, leftX + 20, sy, panelW - 40, 'Enemies on screen', String(this.game.lastEnemies)); sy += 26;
    const bestScore = Profile.getHighScore(this.game.diff);
    this.drawStatRow(g, leftX + 20, sy, panelW - 40, 'Best score', String(bestScore));

    // Right — Upgrades
    PageRenderer.drawPanel(g, rightX, statsY, panelW, 196);
    g.setFont(PageRenderer.HEADING_FONT);
    g.setColor(PageRenderer.ACCENT);
    g.drawString('Upgrades', rightX + 20, statsY + 32);
    g.setColor(PageRenderer.BORDER);
    g.fillRect(rightX + 20, statsY + 44, panelW - 40, 1);

    sy = statsY + 70;
    this.drawStatRow(g, rightX + 20, sy, panelW - 40, 'Health upgrades', String(this.game.lastHealthUps)); sy += 26;
    this.drawStatRow(g, rightX + 20, sy, panelW - 40, 'Speed upgrades', String(this.game.lastSpeedUps)); sy += 26;
    this.drawStatRow(g, rightX + 20, sy, panelW - 40, 'Health refills', String(this.game.lastRefills)); sy += 26;
    this.drawStatRow(g, rightX + 20, sy, panelW - 40, 'Total purchases', String(this.game.lastUpgrades)); sy += 26;

    // Perks used
    const p1 = Perks.getEquipped(0), p2 = Perks.getEquipped(1);
    const perksUsed = (p1 < 0 && p2 < 0) ? 'None' :
      (p1 >= 0 && p2 >= 0) ? Perks.NAMES[p1] + ', ' + Perks.NAMES[p2] :
        (p1 >= 0) ? Perks.NAMES[p1] : Perks.NAMES[p2];
    this.drawStatRow(g, rightX + 20, sy, panelW - 40, 'Perks', perksUsed);

    // XP panel — below stats
    const xpY = statsY + 210;
    const xpW = Game.WIDTH - 120;
    PageRenderer.drawPanel(g, 60, xpY, xpW, 52);

    g.setFont(PageRenderer.HEADING_FONT);
    g.setColor(PageRenderer.ACCENT);
    g.drawString('+' + this.game.lastXpEarned + ' XP', 80, xpY + 24);
    // Coins earned
    g.setColor(new Color(255, 210, 80));
    const coinStr = '+' + this.game.lastCoinsEarned + ' coins';
    g.drawString(coinStr, 260, xpY + 24);

    // Level + XP bar
    g.setFont(PageRenderer.LABEL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    const lvlStr = 'LVL ' + Profile.getLevel();
    g.drawString(lvlStr, 80, xpY + 44);

    const xpBarX = 140;
    const xpBarW = xpW - 100;
    const xpBarH = 8;
    const xpBarY = xpY + 36;
    g.setColor(new Color(22, 30, 44));
    g.fillRoundRect(xpBarX, xpBarY, xpBarW, xpBarH, 4, 4);
    const xpPct = Profile.levelProgress();
    const xpFillW = Math.trunc(xpBarW * xpPct);
    if (xpFillW > 0) {
      g.setColor(PageRenderer.ACCENT);
      g.fillRoundRect(xpBarX, xpBarY, xpFillW, xpBarH, 4, 4);
    }
    g.setColor(PageRenderer.BORDER);
    g.drawRoundRect(xpBarX, xpBarY, xpBarW, xpBarH, 4, 4);

    // Level-up notification
    if (this.game.lastLeveledUp) {
      g.setFont(PageRenderer.BUTTON_FONT);
      g.setColor(new Color(255, 210, 80));
      const lupStr = 'LEVEL UP!  LVL ' + Profile.getLevel();
      fm = g.getFontMetrics();
      g.drawString(lupStr, 60 + xpW - fm.stringWidth(lupStr) - 20, xpY + 28);
    }

    // Achievement count
    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    const achStr = Achievements.getUnlockedCount() + '/' + Achievements.getCount() + ' achievements';
    g.drawString(achStr, 80, xpY + 44 + 14);

    // Buttons
    PageRenderer.drawPrimaryButton(g, Menu.retryX(), Menu.RETRY_Y, Menu.RETRY_W, Menu.RETRY_H, 'Try Again', this.retryH);
    PageRenderer.drawBackButton(g, this.backH);
  }

  private drawStatRow(g: Graphics2D, x: number, y: number, w: number, label: string, value: string): void {
    g.setFont(PageRenderer.BODY_FONT);
    g.setColor(PageRenderer.TEXT_SEC);
    g.drawString(label, x, y);
    const fm: FontMetrics = g.getFontMetrics();
    g.setColor(PageRenderer.TEXT);
    g.drawString(value, x + w - fm.stringWidth(value), y);
  }

  private getRating(score: number, level: number): string {
    if (level >= 20) return '"Legendary"';
    if (level >= 15) return '"Untouchable"';
    if (level >= 10) return '"Veteran"';
    if (level >= 7) return '"Survivor"';
    if (level >= 5) return '"Fighter"';
    if (level >= 3) return '"Rookie"';
    return '"Beginner"';
  }

  // ---------- Pause Menu ----------

  private renderPauseMenu(g: Graphics2D): void {
    // Title
    g.setFont(PageRenderer.TITLE_FONT);
    g.setColor(PageRenderer.ACCENT);
    const pulse = Math.sin(performance.now() * 1e6 / 400_000_000.0) * 0.3 + 0.7;
    g.setColor(new Color(78, 205, 196, Math.trunc(255 * pulse)));
    const title = 'Paused';
    const fm: FontMetrics = g.getFontMetrics();
    g.drawString(title, Math.trunc((Game.WIDTH - fm.stringWidth(title)) / 2), 250);

    // Buttons
    const px = Menu.pauseX();
    PageRenderer.drawPrimaryButton(g, px, Menu.PAUSE_Y0, Menu.PAUSE_W, Menu.PAUSE_H, 'Resume', this.btn[0]);
    PageRenderer.drawSecondaryButton(g, px, Menu.PAUSE_Y0 + Menu.PAUSE_SP, Menu.PAUSE_W, Menu.PAUSE_H, 'Main Menu', this.btn[1]);
    PageRenderer.drawSecondaryButton(g, px, Menu.PAUSE_Y0 + Menu.PAUSE_SP * 2, Menu.PAUSE_W, Menu.PAUSE_H, 'Settings', this.pauseBtn[3]);
    PageRenderer.drawDangerButton(g, px, Menu.PAUSE_Y0 + Menu.PAUSE_SP * 3, Menu.PAUSE_W, Menu.PAUSE_H, 'Quit Game', this.btn[2]);

    // Hint
    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(PageRenderer.TEXT_MUTED);
    const hint = 'Press ESC to resume';
    const fm2: FontMetrics = g.getFontMetrics();
    g.drawString(hint, Math.trunc((Game.WIDTH - fm2.stringWidth(hint)) / 2), Menu.PAUSE_Y0 + Menu.PAUSE_SP * 3 + Menu.PAUSE_H + 30);
  }

  // ==================== Touch support ====================
  //
  // Mobile has no wheel and no hover, and a tap that fires on pointer-DOWN cannot be told
  // apart from the start of a scroll drag — which on the Coin Shop page would mean dragging
  // to scroll silently buys an item. Game routes touch through these three hooks to fix that.

  /** The pages that scroll. Mirrors exactly the states mouseWheelMoved() acts on. */
  public static isScrollableState(s: STATE): boolean {
    return s === STATE.HelpENG || s === STATE.HelpNLD || s === STATE.HelpDEU
        || s === STATE.Settings || s === STATE.Statistics
        || s === STATE.AchievementsPage || s === STATE.CoinShopPage
        || s === STATE.Update_Notes;
  }

  /** Touch drag-to-scroll. `dy` is in logical pixels; same targets the wheel writes. */
  public scrollBy(dy: number): void {
    const s = Game.gameState as STATE;
    if (s === STATE.HelpENG || s === STATE.HelpNLD || s === STATE.HelpDEU) {
      this.helpScrollTarget = Math.max(0, this.helpScrollTarget + dy);
    }
    if (s === STATE.Settings || s === STATE.Statistics
        || s === STATE.AchievementsPage || s === STATE.CoinShopPage
        || s === STATE.Update_Notes) {
      this.settingsScrollTarget = Math.max(0, this.settingsScrollTarget + dy);
    }
  }

  /**
   * Is this point on the settings volume slider? A slider needs its drag to begin on
   * pointer-down, so it is the one thing on a scrollable page that cannot be deferred to
   * pointer-up. Kept in sync with the slider hit-test in handleSettingsClick().
   */
  public hitsAnySlider(mx: number, my: number): boolean {
    if ((Game.gameState as STATE) !== STATE.Settings) return false;
    if (this.confirmAction !== null) return false;
    const contentTop = 130;
    const contentBottom = Game.HEIGHT - 30;
    // Same clip guard handleSettingsClick applies before its slider test: scrolled out of the
    // content area the slider is not drawn and not clickable, so it must not claim the touch.
    if (my < contentTop || my > contentBottom) return false;
    const y = contentTop - Math.trunc(this.settingsScroll);
    const sliderY0 = y + 52 + Math.trunc(Menu.SET_ROW_H / 2) - Math.trunc(Menu.SET_SLIDER_H / 2);
    return this.hitSlider(mx, my, sliderY0);
  }

  public isSliderDragging(): boolean {
    return this.settingsDrag !== SettingsDrag.NONE;
  }
}
